import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import { useMode } from '../contexts/ModeContext';
import API_BASE_URL, { API_ENDPOINTS } from '../config';
import PageTemplate from './PageTemplate';
import DisplayModal, { ViewMode, SortOption, SortDirection } from './DisplayModal';
import SearchModal, { StockFilter } from './SearchModal';
import { InventoryContent } from './InventoryContent';
import { SwipeAction } from './inventory/SwipeableItemCard';
import { QuantityEditModal } from './inventory/QuantityEditModal';
import { UnifiedItemEditModal, EditableItem } from './itemEdit';
import { SavedItem } from './inventory/ItemCard';
import BulkEditModal from './BulkEditModal';
import PhotoReplacementModal from './PhotoReplacementModal';
import SharedCollectionHeader from './SharedCollectionHeader';
import ImageSelectionModal from './ImageSelectionModal';
import { getColorFromName, getInitials } from '../utils/imageHelpers';
import { getCachedImages, setCachedImages, getCachedPhotoData, setCachedPhotoData } from '../utils/imageSearchCache';
import ManageCollectionsModal from './inventory/ManageCollectionsModal';
import UnifiedInventoryMenuBar from './inventory/UnifiedInventoryMenuBar';
import { UnifiedHeader } from './UnifiedHeader';
import CollectionModal from './CollectionModal';
import CollectionShareModal from './CollectionShareModal';
import CollectionSelector, { PSEUDO_COLLECTIONS, CollectionType } from './inventory/CollectionSelector';
import ShareButton from './ShareButton';
import ManageSharingModal from './ManageSharingModal';
import { QRAssignModal } from './QRAssignModal';
import { Toast } from './Toast';
import { useContent } from '../hooks/useContent';
import { useInventoryState, useItemCounts } from '../hooks/useInventoryState';
import { useInventoryCounts } from '../hooks/useInventoryCounts';
import {
  getCachedItems,
  setCachedItems,
  validateItemsCount,
  invalidateCollectionCache,
  invalidateCountsCache,
  prefetchRecentCollections,
} from '../utils/inventoryCache';
import { useItemEdit } from '../hooks/useItemEdit';
import { useItemSelection } from '../hooks/useItemSelection';
import { useContextMenu } from '../hooks/useContextMenu';
import { useResponsive } from '../hooks/useResponsive';
import { useInventorySync } from '../hooks/useInventorySync';
import { recordNetworkFailure, recordNetworkSuccess } from '../hooks/useNetworkRecovery';
import { saveItem } from '../services/inventoryService';
import ContextHelpTip from './ContextHelpTip';
import { Platform } from '../utils/platform';
import EmptyState from './EmptyState';
import AddItemModal, { NewItemData } from './AddItemModal';
import FloatingActionBar from './FloatingActionBar';
import AddMissingItemsModal from './AddMissingItemsModal';
import { hasAIConsent } from './AIConsentModal';
import { parseSearchGroups, itemMatchesSearch, type ParsedTerm } from '../utils/searchParser';
import DuplicateItemDialog, { DuplicateInfo } from './DuplicateItemDialog';
import AuctionSetupModal from './AuctionSetupModal';
import '../styles/components/inventory.css';
import '../styles/components/mobileItemEdit.css';

// Helper function to truncate text to max length at last whole word
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  
  // Find the last space before maxLength
  const lastSpaceIndex = text.lastIndexOf(' ', maxLength - 1);
  if (lastSpaceIndex === -1) {
    // No space found, just truncate and add ellipsis
    return text.substring(0, maxLength - 3) + '...';
  }
  
  // Truncate at last space and add ellipsis
  return text.substring(0, lastSpaceIndex) + '...';
};

// Extend Window interface for pending purchase dates
declare global {
  interface Window {
    pendingPurchaseDates?: { [itemId: number]: string };
  }
}


const normalizeDriveUrl = (url?: string | null) => {
  if (!url) return url || null;
  
  // Convert GCS URLs to HTTPS URLs for browser compatibility
  if (url.startsWith('gs://')) {
    return url.replace('gs://', 'https://storage.googleapis.com/');
  }
  
  try {
    const u = new URL(url);
    if (u.hostname.includes('drive.google.com') && u.pathname.includes('/uc')) {
      // ensure export=view for embeddable images
      u.searchParams.set('export', 'view');
      return u.toString();
    }
  } catch {}
  return url;
};

const COLLECTION_PROPS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const collectionPropsCache = new Map<string, {
  properties: object;
  name: string;
  description: string;
  timestamp: number;
}>();

const dollarsToCents = (dollars: string): number => {
  const num = parseFloat(dollars.replace(/[^0-9.-]/g, ''));
  return Math.round((isNaN(num) ? 0 : num) * 100);
};

interface InventoryViewProps {
  isActive?: boolean; // Whether this tab is currently active
  onInventoryCountChange?: (totalCount: number, filteredCount: number, selectedCount: number, sharedCount: number, completedCount: number, isSelectMode: boolean) => void;
  refreshTrigger?: number; // Increment this to trigger a refresh
  onStockEditStateChange?: (isEditing: boolean, hasUnsavedChanges: boolean) => void;
  onVoiceListeningChange?: (isListening: boolean) => void;
  navigateToCollection?: number | null; // Collection ID to navigate to
  filterByGroupId?: number | null; // Group ID to filter shared items
  // Optional limit handler from AppContent so Inventory saves can surface
  // the same UpgradePromptModal used on the Scan page.
  checkResponseForLimit?: (response: Response) => Promise<boolean>;
  // Header props
  onFontSizeToggle?: () => void;
  fontSize?: string;
  onSockClick?: () => void;
  isVoiceListening?: boolean;
  // Notification navigation callbacks
  onNotificationNavigateToCollection?: (collectionId: number) => void;
  onNotificationNavigateToGroup?: (groupId: number) => void;
  // Callback to register the toggle collection sheet function
  onRegisterToggleSheet?: (toggleFn: () => void) => void;
  // Mode props
  lockedCollectionId?: number | null;
  onOpenAppContext?: () => void;
  onOpenCollectionSheet?: () => void;
  pendingRecipe?: { name: string; description: string | null; search_string: string } | null;
  hideKitchenCollections?: boolean;
}

const InventoryView: React.FC<InventoryViewProps> = ({ 
  isActive = true,
  onInventoryCountChange, 
  refreshTrigger, 
  onStockEditStateChange, 
  onVoiceListeningChange, 
  navigateToCollection, 
  filterByGroupId, 
  checkResponseForLimit,
  onFontSizeToggle,
  fontSize,
  onSockClick,
  isVoiceListening,
  onNotificationNavigateToCollection,
  onNotificationNavigateToGroup,
  onRegisterToggleSheet,
  lockedCollectionId,
  onOpenAppContext,
  onOpenCollectionSheet,
  pendingRecipe,
  hideKitchenCollections
}) => {
  
  const { token, user } = useAuth();
  const { activeSpace, spaces, loading: spacesLoading } = useSpace();
  const { activeMode, groceryCollectionId } = useMode();
  const { inventory } = useContent();
  const [items, setItems] = useState<SavedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // AbortController ref to cancel pending fetch requests when collection changes
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  
  // Keep track of last non-empty items to show during transitions
  // Store both items and the collection they belong to
  const lastItemsRef = useRef<{ items: SavedItem[]; collection: string }>({ items: [], collection: '' });
  
  // NEW: Inventory collections state
  const {
    state: inventoryState,
    setCollection,
    setMode,
    toggleItemSelection,
    selectAll: selectAllItems,
    clearSelection: clearSelectionState
  } = useInventoryState();
  
  const itemCounts = useItemCounts(items);
  
  // Voice toggle ref for direct invocation from sock click (preserves iOS user gesture)
  const voiceToggleRef = useRef<(() => void) | null>(null);
  
  // Internal refresh trigger for collection properties
  const [internalRefreshTrigger, setInternalRefreshTrigger] = useState(0);
  
  // Collection modal state (moved from UnifiedInventoryMenuBar)
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | undefined>(undefined);
  const [selectedCollectionName, setSelectedCollectionName] = useState<string>('');
  const [collectionRefreshTrigger, setCollectionRefreshTrigger] = useState(0);
  const [sharingCollectionId, setSharingCollectionId] = useState<number | null>(null);
  const [sharingCollectionType, setSharingCollectionType] = useState<string | undefined>(undefined);
  
  // Sync inventoryState selections with our selection hook
  useEffect(() => {
    // When inventoryState.selectedItems changes, sync to our hook
    const stateItems = inventoryState.selectedItems;
    if (stateItems.length > 0) {
      // Clear and re-add all items
      clearSelection();
      stateItems.forEach(id => toggleSelection(id));
    } else if (selectedIds.size > 0) {
      // If state is empty but hook has selections, clear hook
      clearSelection();
    }
  }, [inventoryState.selectedItems]); // Intentionally not including hook methods to avoid loops
  
  // Sync mode with activeTab
  useEffect(() => {
    setActiveTab(inventoryState.mode === 'view' ? 'view' : 'bulk');
  }, [inventoryState.mode]);
  
  // Search and filter state - persist in localStorage (space-aware)
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      const key = activeSpace ? `inventory-searchTerm-s${activeSpace.id}` : 'inventory-searchTerm';
      return localStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  });
  const [activeRecipeName, setActiveRecipeName] = useState<string | null>(null);

  // Date filter (calendar date based on created_at, updated_at, or purchased_at). Empty string means "no date filter".
  const [dateFilter, setDateFilter] = useState<string>('');
  const [dateFieldType, setDateFieldType] = useState<'created' | 'updated' | 'purchased'>('created');
  const [selectedCategory, setSelectedCategory] = useState(() => {
    try {
      const key = activeSpace ? `inventory-selectedCategory-s${activeSpace.id}` : 'inventory-selectedCategory';
      return localStorage.getItem(key) || 'all';
    } catch (e) {
      return 'all';
    }
  });
  // New multi-category filter (array of category IDs)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [allCategories, setAllCategories] = useState<Array<{id: number, key: string, label: string, parent_key: string | null}>>([]);
  const [imageFilterId, setImageFilterId] = useState<number | null>(null);
  
  // Location filters
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  
  const [stockFilter, setStockFilter] = useState<StockFilter>(() => {
    try {
      const key = activeSpace ? `inventory-stockFilter-s${activeSpace.id}` : 'inventory-stockFilter';
      return (localStorage.getItem(key) as StockFilter) || 'off';
    } catch (e) {
      return 'off';
    }
  });
  
  // Update local filter state when space changes
  useEffect(() => {
    if (activeSpace) {
      const termKey = `inventory-searchTerm-s${activeSpace.id}`;
      const catKey = `inventory-selectedCategory-s${activeSpace.id}`;
      const stockKey = `inventory-stockFilter-s${activeSpace.id}`;
      
      setSearchTerm(localStorage.getItem(termKey) || '');
      setSelectedCategory(localStorage.getItem(catKey) || 'all');
      setStockFilter((localStorage.getItem(stockKey) as StockFilter) || 'off');
    }
  }, [activeSpace]);
  
  // Shopping list mode: Manage or Shop view
  const [stockChecklistMode, setStockChecklistMode] = useState<'shop' | 'manage'>('shop');
  const [showAllStockChecklistItems, setShowAllStockChecklistItems] = useState(true);
  
  // Track items that are fading out after being checked in Shop mode
  const [fadingOutItems, setFadingOutItems] = useState<Set<number>>(new Set());
  
  // Track items pending re-sort (to show visual feedback before sorting)
  const [itemsPendingSort, setItemsPendingSort] = useState<Set<number>>(new Set());
  
  // Track when header sock should tumble (triggered by checkbox clicks)
  const [isSockTumbling, setIsSockTumbling] = useState(false);
  const [isSockTumblingBackward, setIsSockTumblingBackward] = useState(false);

  // Save filter states to localStorage when changed (space-aware)
  useEffect(() => {
    try {
      const key = activeSpace ? `inventory-searchTerm-s${activeSpace.id}` : 'inventory-searchTerm';
      localStorage.setItem(key, searchTerm);
    } catch (e) {
      // Ignore localStorage errors (can happen in iOS Safari private mode)
    }
  }, [searchTerm, activeSpace]);

  useEffect(() => {
    try {
      const key = activeSpace ? `inventory-selectedCategory-s${activeSpace.id}` : 'inventory-selectedCategory';
      localStorage.setItem(key, selectedCategory);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [selectedCategory, activeSpace]);

  useEffect(() => {
    try {
      const key = activeSpace ? `inventory-stockFilter-s${activeSpace.id}` : 'inventory-stockFilter';
      localStorage.setItem(key, stockFilter);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [stockFilter, activeSpace]);
  
  // Item edit hook - note: fetchItems is passed as onSuccess callback
  const {
    editItem,
    editOpen,
    saving,
    editItemRef,
    setEditItem,
    setEditOpen,
    setSaving,
    openEdit,
    closeEdit,
    updateEditItem,
    saveEditItem,
  } = useItemEdit({
    token,
    onSuccess: async () => {
      // fetchItems will be available here due to closure
      if (typeof fetchItems === 'function') {
        await fetchItems();
      }
    },
    checkResponseForLimit,
  });
  
  // Responsive hook - replaces manual window.innerWidth checks
  const { isMobile } = useResponsive();
  const [showDeleteModalTrigger, setShowDeleteModalTrigger] = useState(false);
  const [showPhotoReplacementModal, setShowPhotoReplacementModal] = useState(false);
  const [replacePhotoScope, setReplacePhotoScope] = useState<'one' | 'all' | null>(null);
  const [replacePhotoItemCount, setReplacePhotoItemCount] = useState(1);
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [quantityEditItem, setQuantityEditItem] = useState<SavedItem | null>(null);
  const [swipeConfirmItem, setSwipeConfirmItem] = useState<{ item: SavedItem; action: 'delete' | 'remove-from-collection' } | null>(null);
  const [addToCollectionItem, setAddToCollectionItem] = useState<SavedItem | null>(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemModalDefaultTab, setAddItemModalDefaultTab] = useState<'scan' | 'receipt' | 'manual'>('scan');
  const [showAddMissingModal, setShowAddMissingModal] = useState(false);
  
    const [duplicateDialogInfo, setDuplicateDialogInfo] = useState<DuplicateInfo | null>(null);
  const [scrollToItemId, setScrollToItemId] = useState<number | null>(null);
  const pendingScrollItemIdRef = useRef<number | null>(null);
  const [editModalSearchImages, setEditModalSearchImages] = useState<string[]>([]);
  const [editModalPhotoData, setEditModalPhotoData] = useState<any[]>([]); // Full Unsplash photo objects
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchPage, setImageSearchPage] = useState(1);
  const [imageSearchTermOverride, setImageSearchTermOverride] = useState<string>('');
  const [isLoadingMoreImages, setIsLoadingMoreImages] = useState(false);
  const prevImageSelectionModalRef = useRef(false);
  
  // Tab and selection state
  const [activeTab, setActiveTab] = useState<'view' | 'bulk'>('view');
  
  // Selection hook
  const {
    selectedIds,
    isSelectMode,
    toggleSelection,
    selectAll,
    clearSelection,
    enterSelectMode,
    exitSelectMode,
    isSelected,
    getSelectedCount,
    getSelectedItems,
  } = useItemSelection();
  
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showAddToCollectionModal, setShowAddToCollectionModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false); // Lock state for view/edit mode
  
  // Collection submenu state (for context menu)
  const [showCollectionSubmenu, setShowCollectionSubmenu] = useState(false);
  const [collectionActionMode, setCollectionActionMode] = useState<'add' | 'move' | null>(null);
  
  // Collection properties for display mode detection
  const [collectionProperties, setCollectionProperties] = useState<{
    id?: number;
    collection_type?: 'generic' | 'checklist' | 'stock' | 'shopping' | 'marketplace' | 'grocery';
    shared_with_count?: number;
    has_guest_access?: boolean;
    permission?: 'view' | 'edit' | 'full_edit';
    owner_id?: number;
    owner_name?: string;
    owner_email?: string;
    marketplace_visible?: boolean;
  }>({});
  
  // Ref to track current collection properties for use in callbacks
  const collectionPropertiesRef = useRef(collectionProperties);
  useEffect(() => {
    collectionPropertiesRef.current = collectionProperties;
  }, [collectionProperties]);
  
  // Collection name and description for display
  const [collectionName, setCollectionName] = useState<string>('');
  const [collectionDescription, setCollectionDescription] = useState<string>('');
  
  // Active bindle (passed up from menu bar)
  const [activeBindle, setActiveBindle] = useState<{ name: string; description: string | null; search_string: string } | null>(null);
  const [bindleDescExpanded, setBindleDescExpanded] = useState(false);
  
  // Use the new unified counts hook with caching
  const { counts, refetchCounts } = useInventoryCounts(refreshTrigger);
  
  // Extract individual counts for backward compatibility
  const wanderingItemsCount = counts?.wandering || 0;
  const allItemsCount = counts?.all || 0;
  
  // Restore scroll position when component mounts
  useEffect(() => {
    const scrollContainer = document.querySelector('.tab-scroll-container') as HTMLElement;
    if (!scrollContainer) return;
    
    const savedPosition = sessionStorage.getItem('scroll-position-inventory');
    if (savedPosition) {
      const targetPosition = parseInt(savedPosition, 10);
      // Multiple attempts to handle content loading delays
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = targetPosition;
      });
      setTimeout(() => {
        scrollContainer.scrollTop = targetPosition;
      }, 100);
      setTimeout(() => {
        scrollContainer.scrollTop = targetPosition;
      }, 300);
    }
  }, []); // Run once on mount
  
  // Save scroll position continuously while viewing inventory (before unmount on mobile)
  useEffect(() => {
    // Find the tab-scroll-container for inventory (parent of this component)
    const scrollContainer = document.querySelector('.tab-scroll-container') as HTMLElement;
    if (!scrollContainer || editOpen || showImageSelectionModal || !isActive) return;
    
    const saveScrollPosition = () => {
      try {
        const position = scrollContainer.scrollTop;
        if (position > 0) {
          sessionStorage.setItem('scroll-position-inventory', String(position));
        }
      } catch (e) {
        // Ignore storage errors
      }
    };
    
    // Save on scroll
    scrollContainer.addEventListener('scroll', saveScrollPosition);
    
    // Save before unmount
    return () => {
      saveScrollPosition();
      scrollContainer.removeEventListener('scroll', saveScrollPosition);
    };
  }, [editOpen, showImageSelectionModal, isActive]);
  
  // Save scroll position immediately when modal opens (iOS fix)
  useEffect(() => {
    if (showImageSelectionModal) {
      const mainContent = document.querySelector('.main-content') as HTMLElement;
      if (mainContent) {
        try {
          const position = mainContent.scrollTop;
          if (position > 0) {
            sessionStorage.setItem('inventory-scroll-position', String(position));
          }
        } catch (e) {
          // Ignore storage errors
        }
      }
    }
  }, [showImageSelectionModal]);
  
  // Restore scroll position when image selection modal closes
  useEffect(() => {
    const wasModalOpen = prevImageSelectionModalRef.current;
    const isModalClosing = wasModalOpen && !showImageSelectionModal;
    
    if (isModalClosing && !editOpen) {
      const restoreScroll = () => {
        const mc = document.querySelector('.main-content') as HTMLElement;
        if (mc) {
          try {
            const savedPosition = sessionStorage.getItem('inventory-scroll-position');
            if (savedPosition) {
              const targetPosition = parseInt(savedPosition, 10);
              requestAnimationFrame(() => {
                mc.scrollTop = targetPosition;
                requestAnimationFrame(() => {
                  if (mc.scrollTop !== targetPosition) {
                    mc.scrollTop = targetPosition;
                  }
                });
              });
            }
          } catch (e) {
            // Ignore storage errors
          }
        }
      };
      
      // Multiple restoration attempts to handle iOS Safari scroll resets after re-render
      const timeoutId1 = setTimeout(restoreScroll, 100);
      const timeoutId2 = setTimeout(restoreScroll, 500);
      const timeoutId3 = setTimeout(restoreScroll, 800);
      
      prevImageSelectionModalRef.current = showImageSelectionModal;
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
    
    prevImageSelectionModalRef.current = showImageSelectionModal;
  }, [editOpen, showImageSelectionModal]);
  
  // Update lock state based on current item's access role
  useEffect(() => {
    if (editItem && editOpen) {
      const currentItemData = items.find(i => i.id === editItem.id);
      if (currentItemData?.my_role === 'viewer') {
        setIsLocked(true);
      } else {
        // Default to unlocked for non-viewer items
        setIsLocked(false);
      }
    } else {
      // When no item is being edited, clear the lock state
      setIsLocked(false);
    }
  }, [editItem, editOpen, items]);
  
  // Stock mode editing state
  const [stockEditMode, setStockEditMode] = useState(false);
  const [pendingQuantityChanges, setPendingQuantityChanges] = useState<Record<number, number>>({});
  
  // Auto-save undo state for shopping list modes
  type UndoAction = {
    type: 'quantity' | 'check';
    itemId: number;
    previousValue: number | boolean;
    newValue: number | boolean;
    timestamp: number;
  };
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [undoVisible, setUndoVisible] = useState(false);
  const undoTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Notify parent of stock edit state changes
  useEffect(() => {
    if (onStockEditStateChange) {
      const hasUnsavedChanges = Object.keys(pendingQuantityChanges).length > 0;
      onStockEditStateChange(stockEditMode, hasUnsavedChanges);
    }
  }, [stockEditMode, pendingQuantityChanges, onStockEditStateChange]);
  
  // Helper to show undo button with auto-hide timer
  const showUndoWithTimer = useCallback((action: UndoAction) => {
    // Clear existing timer
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
    }
    
    // Add to undo stack (keep last 10 actions)
    setUndoStack(prev => [...prev.slice(-9), action]);
    setUndoVisible(true);
    
    // Auto-hide after 5 seconds
    undoTimerRef.current = setTimeout(() => {
      setUndoVisible(false);
      setUndoStack([]);
    }, 5000);
  }, []);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }
    };
  }, []);
  
  // Modal states
  const [showDisplayModal, setShowDisplayModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showManageSharingModal, setShowManageSharingModal] = useState(false);
  const [manageSharingItemName, setManageSharingItemName] = useState<string | undefined>(undefined);
  const [manageSharingItemIds, setManageSharingItemIds] = useState<number[]>([]);
  const [showQRAssignModal, setShowQRAssignModal] = useState(false);
  const [showAuctionSetupModal, setShowAuctionSetupModal] = useState(false);
  const [auctionSetupItemId, setAuctionSetupItemId] = useState<number | null>(null);
  const [auctionSetupItemName, setAuctionSetupItemName] = useState('');
  const [auctionSetupSalePrice, setAuctionSetupSalePrice] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'orange' | 'gray' | undefined>(undefined);
  const [showToast, setShowToast] = useState(false);
  
  // Context menu hook
  const {
    contextItem,
    showContextMenu,
    menuPosition,
    openContextMenu,
    closeContextMenu,
    setContextItem,
  } = useContextMenu();
  
  // Reset collection submenu when context menu closes
  useEffect(() => {
    if (!showContextMenu) {
      setShowCollectionSubmenu(false);
    }
  }, [showContextMenu]);
  
  // Listen for sock icon click to trigger voice search directly in menu bar
  // Uses voiceToggleRef for direct synchronous invocation (preserves iOS user gesture)
  useEffect(() => {
    const handleSockIconClick = () => {
      setSearchTerm(''); // Clear search
      // Call voice toggle directly (keeps recognition.start() in user gesture call stack)
      if (voiceToggleRef.current) {
        voiceToggleRef.current();
      } else {
        // Fallback to custom event if ref not yet registered
        const event = new CustomEvent('startVoiceSearch');
        window.dispatchEvent(event);
      }
    };
    
    window.addEventListener('sockIconClick', handleSockIconClick);
    return () => window.removeEventListener('sockIconClick', handleSockIconClick);
  }, []);
  
  // Display options - persist in localStorage per collection
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(`inventory-viewMode-${inventoryState.collection}`);
    return (saved as ViewMode) || (inventoryState.collection === 'stock' ? 'stock-list' : 'card');
  });
  
  // Thumbnail size for composition view - controls everything (layout + details)
  const [thumbnailSize, setThumbnailSize] = useState<'small' | 'medium' | 'large' | 'x-large'>(() => {
    const saved = localStorage.getItem('inventory-thumbnailSize');
    return (saved as 'small' | 'medium' | 'large' | 'x-large') || 'medium';
  });
  
  const handleThumbnailSizeChange = useCallback((size: 'small' | 'medium' | 'large' | 'x-large') => {
    setThumbnailSize(size);
    localStorage.setItem('inventory-thumbnailSize', size);
  }, []);

  // Listen for thumbnail size changes from View page
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'inventory-thumbnailSize' && e.newValue) {
        setThumbnailSize(e.newValue as 'small' | 'medium' | 'large' | 'x-large');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Helper to get default sort for a collection
  const getDefaultSort = useCallback((collection: CollectionType, collectionProps: any): { sortBy: SortOption; sortDirection: SortDirection } => {
    // Shopping list collections default to 'shopping-list' sort
    if (collectionProps?.collection_type === 'shopping' || collectionProps?.collection_type === 'grocery' || viewMode.startsWith('stock-checklist-')) {
      return { sortBy: 'shopping-list', sortDirection: 'asc' };
    }
    // All other collections default to 'name' ascending
    return { sortBy: 'name', sortDirection: 'asc' };
  }, [viewMode]);

  const [sortBy, setSortBy] = useState<SortOption>(() => {
    // Try per-collection preference first
    const saved = localStorage.getItem(`inventory-sort-${inventoryState.collection}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.sortBy || 'name';
      } catch (e) {
        // Fall through to default
      }
    }
    // Fall back to global preference (for backward compatibility)
    const globalSaved = localStorage.getItem('inventory-sortBy');
    return (globalSaved as SortOption) || 'name';
  });
  
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    // Try per-collection preference first
    const saved = localStorage.getItem(`inventory-sort-${inventoryState.collection}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.sortDirection || 'asc';
      } catch (e) {
        // Fall through to default
      }
    }
    // Fall back to global preference (for backward compatibility)
    const globalSaved = localStorage.getItem('inventory-sortDirection');
    return (globalSaved as SortDirection) || 'asc';
  });

  // Wrapper to mark view mode changes as explicit user actions
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    
    // Save immediately when user explicitly changes
    localStorage.setItem(`inventory-viewMode-${inventoryState.collection}`, mode);
    localStorage.setItem(`inventory-viewMode-explicit-${inventoryState.collection}`, 'true');
    
    // Update global display format preference
    const displayFormat = (
      mode === 'list' || 
      mode === 'stock-list' || 
      mode === 'checklist-list' || 
      mode === 'stock-checklist-list'
    ) ? 'list' : 'card';
    localStorage.setItem('inventory-displayFormat', displayFormat);
  }, [inventoryState.collection]);

  // Handle sort changes - save per collection
  const handleSortByChange = useCallback((newSortBy: SortOption) => {
    setSortBy(newSortBy);
    const sortPreference = { sortBy: newSortBy, sortDirection };
    localStorage.setItem(`inventory-sort-${inventoryState.collection}`, JSON.stringify(sortPreference));
    // Keep global for backward compatibility
    localStorage.setItem('inventory-sortBy', newSortBy);
  }, [inventoryState.collection, sortDirection]);

  const handleSortDirectionChange = useCallback((newDirection: SortDirection) => {
    setSortDirection(newDirection);
    const sortPreference = { sortBy, sortDirection: newDirection };
    localStorage.setItem(`inventory-sort-${inventoryState.collection}`, JSON.stringify(sortPreference));
    // Keep global for backward compatibility
    localStorage.setItem('inventory-sortDirection', newDirection);
  }, [inventoryState.collection, sortBy]);

  // Reset sort to default for current collection
  const handleResetThisCollection = useCallback(() => {
    const defaults = getDefaultSort(inventoryState.collection, collectionProperties);
    setSortBy(defaults.sortBy);
    setSortDirection(defaults.sortDirection);
    localStorage.removeItem(`inventory-sort-${inventoryState.collection}`);
    setShowDisplayModal(false);
  }, [inventoryState.collection, collectionProperties, getDefaultSort]);

  // Reset sort to default for ALL collections
  const handleResetAllCollections = useCallback(() => {
    // Clear all per-collection sort preferences
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('inventory-sort-')) {
        localStorage.removeItem(key);
      }
    });
    // Reset current collection to defaults
    const defaults = getDefaultSort(inventoryState.collection, collectionProperties);
    setSortBy(defaults.sortBy);
    setSortDirection(defaults.sortDirection);
    setShowDisplayModal(false);
  }, [inventoryState.collection, collectionProperties, getDefaultSort]);

  // Reload sort preferences when collection changes
  useEffect(() => {
    const saved = localStorage.getItem(`inventory-sort-${inventoryState.collection}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSortBy(parsed.sortBy || 'name');
        setSortDirection(parsed.sortDirection || 'asc');
        return;
      } catch (e) {
        // Fall through to defaults
      }
    }
    
    // No saved preference - apply defaults (name ascending for now, will update when properties load)
    setSortBy('name');
    setSortDirection('asc');
  }, [inventoryState.collection]);

  // Update sort to smart defaults when collection properties are loaded (only if no saved preference)
  useEffect(() => {
    // Only apply smart defaults if there's no saved preference
    const saved = localStorage.getItem(`inventory-sort-${inventoryState.collection}`);
    if (saved) return; // User has a preference, don't override
    
    const defaults = getDefaultSort(inventoryState.collection, collectionProperties);
    setSortBy(defaults.sortBy);
    setSortDirection(defaults.sortDirection);
  }, [collectionProperties, inventoryState.collection, getDefaultSort]);
  
  // Fetch collection properties when collection changes OR when refreshTrigger changes
  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();
    
    const fetchCollectionProperties = async () => {
      // Don't clear collection name immediately - let it update when new data arrives
      // This prevents showing "Wandering Sock" fallback during the fetch
      
      if (!token || inventoryState.collection === 'all' || 
          inventoryState.collection === 'shared_with_me' ||
          inventoryState.collection === 'wandering' ||
          inventoryState.collection.startsWith('shared_with_group_')) {
        setCollectionProperties({});
        setCollectionDescription('');
        return;
      }
      
      
      try {
        let collectionId: string | null = null;
        
        // Extract collection ID from different formats
        if (inventoryState.collection.startsWith('shared-')) {
          collectionId = inventoryState.collection.replace('shared-', '');
        } else if (!isNaN(Number(inventoryState.collection))) {
          collectionId = inventoryState.collection;
        }
        
        if (collectionId) {
          // Serve from in-memory cache immediately if fresh (eliminates lag on space switches)
          const cached = collectionPropsCache.get(collectionId);
          const isCacheFresh = cached && (Date.now() - cached.timestamp < COLLECTION_PROPS_CACHE_TTL);
          if (cached && !cancelled) {
            setCollectionProperties(cached.properties as any);
            setCollectionName(cached.name);
            setCollectionDescription(cached.description);
            delete hasInitializedRef.current[inventoryState.collection];
            if (isCacheFresh) return; // Cache is fresh — skip network call entirely
          }

          // Cache miss or stale — fetch from network
          const url = `${API_ENDPOINTS.GET_COLLECTION(parseInt(collectionId))}`;
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: abortController.signal
          });
          
          if (!cancelled && response.ok) {
            const data = await response.json();
            // Only update if we're still on the same collection (prevent race condition)
            // Compare collection IDs, handling both "123" and "shared-123" formats
            const currentCollectionId = inventoryState.collection.replace('shared-', '');
            if (String(data.id) === currentCollectionId) {
              const props = {
                id: data.id,
                collection_type: data.collection_type || 'generic',
                shared_with_count: data.shared_with_count || 0,
                has_guest_access: data.has_guest_access || false,
                permission: data.permission,
                owner_id: data.user_id,
                owner_name: data.owner_name,
                owner_email: data.owner_email,
                marketplace_visible: data.marketplace_visible || false
              };
              setCollectionProperties(props);
              setCollectionName(data.name || '');
              setCollectionDescription(data.description || '');
              // Populate cache for next visit
              collectionPropsCache.set(collectionId, {
                properties: props,
                name: data.name || '',
                description: data.description || '',
                timestamp: Date.now()
              });
              // Clear initialization flag to force view mode re-evaluation
              delete hasInitializedRef.current[inventoryState.collection];
            }
          } else if (!cancelled && !cached) {
            setCollectionProperties({});
            setCollectionName('');
            setCollectionDescription('');
          }
        } else {
          setCollectionProperties({});
        }
      } catch (error: any) {
        if (error.name !== 'AbortError' && !cancelled) {
          setCollectionProperties({});
        }
      }
    };
    
    fetchCollectionProperties();
    
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [token, inventoryState.collection, refreshTrigger, internalRefreshTrigger]);
  
  
  // Switch to appropriate view mode when collection changes
  // Use refs to track the last collection and if we've initialized
  const lastCollectionRef = React.useRef(inventoryState.collection);
  const hasInitializedRef = React.useRef<{[key: string]: boolean}>({});
  
  useEffect(() => {
    const collectionChanged = lastCollectionRef.current !== inventoryState.collection;
    
    // If collection changed, update ref and clear initialization flag for the NEW collection
    if (collectionChanged) {
      const previousCollection = lastCollectionRef.current;
      lastCollectionRef.current = inventoryState.collection;
      
      // Clear search term when switching collections
      setSearchTerm('');
      
      // Clear initialization flag for the NEW collection so it re-initializes
      delete hasInitializedRef.current[inventoryState.collection];
      
      // Immediately reset properties for pseudo-collections to prevent stale data
      if (inventoryState.collection === 'all' || 
          inventoryState.collection === 'wandering' ||
          inventoryState.collection === 'shared_with_me' ||
          inventoryState.collection.startsWith('shared_with_group_')) {
        setCollectionProperties({});
      }
    }
    
    // For non-pseudo collections, wait for properties to load before setting view mode
    const needsProperties = inventoryState.collection !== 'all' && 
                           inventoryState.collection !== 'wandering' &&
                           inventoryState.collection !== 'shared_with_me' &&
                           !inventoryState.collection.startsWith('shared_with_group_');
    
    // Use empty properties for pseudo-collections (all, wandering, shared_with_me, shared)
    const currentProperties = (inventoryState.collection === 'all' || 
                               inventoryState.collection === 'wandering' ||
                               inventoryState.collection === 'shared_with_me' ||
                               inventoryState.collection.startsWith('shared_with_group_'))
      ? {}
      : collectionProperties;
    
    const hasProperties = Object.keys(currentProperties).length > 0;
    
    // Skip if we've already initialized this specific collection
    if (hasInitializedRef.current[inventoryState.collection]) {
      return;
    }
    
    // Skip if this collection needs properties but they haven't loaded yet
    if (needsProperties && !hasProperties) {
      return;
    }
    
    // Skip if properties are for a different collection (stale data)
    // Handle both "123" and "shared-123" formats
    const collectionIdToCompare = inventoryState.collection.replace('shared-', '');
    if (needsProperties && currentProperties.id && String(currentProperties.id) !== collectionIdToCompare) {
      return;
    }
    
    // Mark this specific collection as initialized
    hasInitializedRef.current[inventoryState.collection] = true;
    
    const saved = localStorage.getItem(`inventory-viewMode-${inventoryState.collection}`);
    const explicitlySet = localStorage.getItem(`inventory-viewMode-explicit-${inventoryState.collection}`) === 'true';
    
    // Get user's preferred display format (list vs card) from global preference
    const globalDisplayPreference = localStorage.getItem('inventory-displayFormat') || 'card'; // 'list' or 'card';
    
    // Determine view mode based on collection properties and global preference
    // Priority: shopping > checklist > stock > default
    let targetViewMode: ViewMode = globalDisplayPreference === 'list' ? 'list' : 'card';
    if (currentProperties.collection_type === 'shopping' || currentProperties.collection_type === 'grocery') {
      targetViewMode = globalDisplayPreference === 'list' ? 'stock-checklist-list' : 'stock-checklist-card';
    } else if (currentProperties.collection_type === 'checklist') {
      targetViewMode = globalDisplayPreference === 'list' ? 'checklist-list' : 'checklist-card';
    } else if (currentProperties.collection_type === 'stock') {
      targetViewMode = globalDisplayPreference === 'list' ? 'stock-list' : 'stock-card';
    } else if (currentProperties.collection_type === 'marketplace') {
      targetViewMode = globalDisplayPreference === 'list' ? 'marketplace-list' : 'marketplace-card';
    }
    
    // If user explicitly set this collection's view, respect it
    // Otherwise, use the global preference
    if (saved && explicitlySet) {
      // Validate that saved view mode is compatible with collection type
      const isStockChecklistMode = saved === 'stock-checklist-card' || saved === 'stock-checklist-list';
      const isStockMode = saved === 'stock-list' || saved === 'stock-card';
      const isChecklistMode = saved === 'checklist-card' || saved === 'checklist-list';
      const isRegularMode = saved === 'card' || saved === 'list';
      const isMarketplaceMode = saved === 'marketplace-card' || saved === 'marketplace-list';
      
      // Use saved mode if compatible with collection type
      if ((currentProperties.collection_type === 'shopping' || currentProperties.collection_type === 'grocery') && isStockChecklistMode) {
        setViewMode(saved as ViewMode);
      } else if (currentProperties.collection_type === 'checklist' && isChecklistMode) {
        setViewMode(saved as ViewMode);
      } else if (currentProperties.collection_type === 'stock' && isStockMode) {
        setViewMode(saved as ViewMode);
      } else if (currentProperties.collection_type === 'marketplace' && isMarketplaceMode) {
        setViewMode(saved as ViewMode);
      } else if (currentProperties.collection_type === 'generic' && isRegularMode) {
        setViewMode(saved as ViewMode);
      } else {
        // Saved mode incompatible, use target mode
        setViewMode(targetViewMode);
      }
    } else {
      // No explicit setting, use global preference
      setViewMode(targetViewMode);
    }
  }, [inventoryState.collection, collectionProperties, token]);

  useEffect(() => {
    localStorage.setItem('inventory-sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('inventory-sortDirection', sortDirection);
  }, [sortDirection]);

  // Sync activeTab with selection mode from hook
  useEffect(() => {
    if (activeTab === 'bulk' && !isSelectMode) {
      enterSelectMode();
    } else if (activeTab === 'view' && isSelectMode) {
      exitSelectMode();
    }
  }, [activeTab, isSelectMode, enterSelectMode, exitSelectMode]);

  const handleItemLongPress = useCallback((item: SavedItem) => {
    // In select mode, long press just toggles selection - no context menu
    if (isSelectMode) {
      toggleSelection(item.id);
      return;
    }

    // Otherwise, show single-item context for the pressed item.
    openContextMenu(item, { x: 0, y: 0 }); // Position doesn't matter for mobile
  }, [isSelectMode, toggleSelection, openContextMenu]);

  // Handle swipe actions on items
  const handleSwipeAction = useCallback(async (item: SavedItem, action: SwipeAction) => {
    if (!token) return;
    
    switch (action) {
      case 'delete': {
        // Show confirmation dialog before deleting
        setSwipeConfirmItem({ item, action: 'delete' });
        break;
      }
      
      case 'remove-from-collection': {
        // Show confirmation dialog before removing
        if (inventoryState.collection === 'all' || !collectionProperties.id) return;
        setSwipeConfirmItem({ item, action: 'remove-from-collection' });
        break;
      }
      
      case 'add-to-collection': {
        // Open collection picker modal for this item
        setAddToCollectionItem(item);
        break;
      }
      
      case 'set-quantity-zero': {
        // Set item quantity to 0
        const previousQuantity = item.quantity ?? 0;
        
        // Optimistically update UI
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: 0 } : i));
        
        try {
          const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: item.id, name: item.name, quantity: 0 }]
            }),
          });
          
          if (resp.ok) {
            showUndoWithTimer({
              type: 'quantity',
              itemId: item.id,
              previousValue: previousQuantity,
              newValue: 0,
              timestamp: Date.now(),
            });
          } else {
            // Revert on failure
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: previousQuantity } : i));
          }
        } catch (error) {
          console.error('Failed to set quantity to zero:', error);
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: previousQuantity } : i));
        }
        break;
      }
      
      case 'decrease-quantity': {
        // Decrease item quantity by 1 (min -1, where -1 = "don't want to restock")
        const previousQuantity = item.quantity ?? 0;
        const newQuantity = Math.max(-1, previousQuantity - 1);
        
        if (newQuantity === previousQuantity) return; // Already at -1
        
        // Start fade-out animation if going to 0 and in shopping list Manage mode with filter on
        if (newQuantity === 0 && viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'manage' && !showAllStockChecklistItems) {
          setFadingOutItems(prev => new Set(prev).add(item.id));
          // Trigger sock tumble animation
          setIsSockTumbling(true);
          setTimeout(() => setIsSockTumbling(false), 600);
          // Wait for animation
          await new Promise(resolve => setTimeout(resolve, 2000));
          // Remove from fading set
          setFadingOutItems(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
        
        // Optimistically update UI immediately (user sees quantity change)
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: newQuantity } : i));
        
        // Show toast notifications for shopping lists
        if (collectionProperties.collection_type === 'shopping') {
          if (newQuantity === 0) {
            setToastMessage(`${item.name}: 0`);
            setToastColor('orange');
            setShowToast(true);
          } else if (newQuantity === -1) {
            setToastMessage(`${item.name}: -1`);
            setToastColor('gray');
            setShowToast(true);
          }
          
          // Find the next item in the current display order BEFORE re-sorting
          const currentItemElement = document.querySelector(`[data-item-id="${item.id}"]`);
          const nextItemElement = currentItemElement?.nextElementSibling;
          const nextItemId = nextItemElement?.getAttribute('data-item-id');
          
          // Auto-scroll to the next item after re-sorting completes
          if (nextItemId) {
            setTimeout(() => {
              const targetElement = document.querySelector(`[data-item-id="${nextItemId}"]`);
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }
        }
        
        try {
          const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: item.id, name: item.name, quantity: newQuantity }]
            }),
          });
          
          if (resp.ok) {
            showUndoWithTimer({
              type: 'quantity',
              itemId: item.id,
              previousValue: previousQuantity,
              newValue: newQuantity,
              timestamp: Date.now(),
            });
          } else {
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: previousQuantity } : i));
          }
        } catch (error) {
          console.error('Failed to decrease quantity:', error);
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: previousQuantity } : i));
        }
        break;
      }
      
      case 'increase-quantity': {
        // Increase item quantity by 1
        const previousQuantity = item.quantity ?? 0;
        const newQuantity = previousQuantity + 1;
        
        // Optimistically update UI
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: newQuantity } : i));
        
        try {
          const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: item.id, name: item.name, quantity: newQuantity }]
            }),
          });
          
          if (resp.ok) {
            showUndoWithTimer({
              type: 'quantity',
              itemId: item.id,
              previousValue: previousQuantity,
              newValue: newQuantity,
              timestamp: Date.now(),
            });
          } else {
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: previousQuantity } : i));
          }
        } catch (error) {
          console.error('Failed to increase quantity:', error);
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: previousQuantity } : i));
        }
        break;
      }
    }
  }, [token, inventoryState.collection, collectionProperties.id, showUndoWithTimer]);

  // Keep ref in sync with state
  useEffect(() => {
    editItemRef.current = editItem;
  }, [editItem]);

  // Responsive detection now handled by useResponsive hook

  // Memoize image metadata to prevent re-renders
  const editImageMeta = React.useMemo(() => {
    if (!editItem) return undefined;
    const src = items.find(i => i.id === editItem.id);
    // For new items (duplicates), try to find metadata from the original image
    if (!src && editItem.id === -1 && (editItem as any).pendingImageId) {
      const imageId = (editItem as any).pendingImageId;
      const itemWithSameImage = items.find(i => i.image_id === imageId);
      if (itemWithSameImage) {
        const w = itemWithSameImage.image_width;
        const h = itemWithSameImage.image_height;
        if (w && h && w > 0 && h > 0) {
          return { width: w, height: h };
        }
      }
      return undefined;
    }
    const w = src?.image_width;
    const h = src?.image_height;
    if (w && h && w > 0 && h > 0) {
      return { width: w, height: h };
    }
    return undefined;
  }, [editItem?.id, items, editItem]);

  // Memoize related count to prevent re-renders
  const editRelatedCount = React.useMemo(() => {
    if (!editItem) return 1;
    const imgId = items.find(i => i.id === editItem.id)?.image_id;
    if (!imgId && imgId !== 0) return 1;
    const count = items.filter(i => i.image_id === imgId).length;
    return Math.max(count, 1);
  }, [editItem?.id, items]);

  // Memoize image ID to prevent re-renders
  const editImageId = React.useMemo(() => {
    if (!editItem) return undefined;
    // For new items (id: -1) with pendingImageId, use that
    if (editItem.id === -1 && (editItem as any).pendingImageId) {
      return (editItem as any).pendingImageId;
    }
    // Use image_id from editItem if available (for copied items)
    if (editItem.image_id !== undefined) {
      return editItem.image_id;
    }
    // Fallback: look up in items array
    const foundItem = items.find(i => i.id === editItem.id);
    return foundItem?.image_id;
  }, [editItem?.id, editItem?.image_id, items, editItem]);
  
  // Fetch all categories for filter chips (only once per session)
  useEffect(() => {
    const loadCategories = async () => {
      if (!token) return;
      // Skip if we already have categories loaded
      if (allCategories.length > 0) return;
      try {
        const { getCategories } = await import('../services/categoryService');
        const cats = await getCategories(token, { flat: true });
        setAllCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, [token, allCategories.length]);
  
  // CSS-only layout: no JS smart scroll
  
  const fetchItems = useCallback(async (silent = false, retryCount = 0): Promise<any[] | null> => {
    console.log(`[InventoryView] fetchItems called, token exists: ${!!token}, silent: ${silent}, retry: ${retryCount}`);
    if (!token) {
      console.log('[InventoryView] No token, skipping fetch');
      return null;
    }
    
    // Note: Cache is already loaded in the useEffect when collection changes
    // This function always fetches fresh data to validate/update the cache
    
    // Cancel any pending fetch request
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    fetchAbortControllerRef.current = abortController;
    
    try {
      // Only show loading spinner if not a silent refresh
      if (!silent) {
        setLoading(true);
      }
      
      let itemsArray: any[] = [];
      
      // Handle merged collections specially - fetch from both sources
      if (inventoryState.collection.startsWith('merged-')) {
        // Parse the IDs: format is "merged-{ownedId}-{sharedId}"
        const ids = inventoryState.collection.replace('merged-', '').split('-');
        const ownedId = ids[0];
        const sharedId = ids[1];
        
        // Fetch from both endpoints in parallel
        const [ownedResponse, sharedResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/collections/${ownedId}/items`, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: abortController.signal
          }),
          fetch(`${API_BASE_URL}/api/collections/${sharedId}/items`, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: abortController.signal
          })
        ]);
        
        const ownedItems = ownedResponse.ok ? await ownedResponse.json() : [];
        const sharedItems = sharedResponse.ok ? await sharedResponse.json() : [];
        
        // Merge items, removing duplicates by ID
        const ownedArray = Array.isArray(ownedItems) ? ownedItems : (ownedItems.items || []);
        const sharedArray = Array.isArray(sharedItems) ? sharedItems : (sharedItems.items || []);
        
        const itemMap = new Map();
        const ownedIds = new Set(ownedArray.map((item: any) => item.id));
        
        // Add owned items
        ownedArray.forEach((item: any) => {
          itemMap.set(item.id, { ...item, _fromOwned: true });
        });
        
        // Add shared items (mark them if not already owned)
        sharedArray.forEach((item: any) => {
          if (!itemMap.has(item.id)) {
            // Ensure shared items have the correct access_type for badge display
            const sharedItem = { 
              ...item, 
              _fromShared: true,
              // If access_type is not already set to a shared type, set it
              access_type: item.access_type || 'shared_with_me'
            };
            itemMap.set(item.id, sharedItem);
          }
        });
        
        itemsArray = Array.from(itemMap.values());
      } 
      // Handle same-collection-shared (owned collection that's also shared)
      else if (!inventoryState.collection.startsWith('shared-') && 
               !inventoryState.collection.startsWith('shared_with_group_') &&
               inventoryState.collection !== 'all' &&
               inventoryState.collection !== 'wandering' &&
               inventoryState.collection !== 'shared_with_me') {
        // This might be an owned collection that's also shared
        // Fetch items and calculate actual counts
        const response = await fetch(`${API_BASE_URL}/api/collections/${inventoryState.collection}/items`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: abortController.signal
        });
        
        if (response.ok) {
          const data = await response.json();
          itemsArray = Array.isArray(data) ? data : (data.items || []);
        }
      } else {
        // Determine which endpoint to use based on collection
        let endpoint = API_ENDPOINTS.GET_ITEMS;
        let groupIdFromCollection = null;
        let accessTypeFilter = null;
        
        // If a specific collection is selected (not 'all'), fetch from collections API
        if (inventoryState.collection !== 'all') {
          // Check if this is "Nomads" pseudo-collection
          if (inventoryState.collection === 'wandering') {
            endpoint = `${API_BASE_URL}/api/items/wandering`;
          }
          // Check if this is "Shared With Me" pseudo-collection
          else if (inventoryState.collection === 'shared_with_me') {
            endpoint = `${API_BASE_URL}/api/sharing/items/my-items`;
            accessTypeFilter = 'shared_with_me';
          }
          // Check if this is a "shared with group" filter
          else if (inventoryState.collection.startsWith('shared_with_group_')) {
            // Extract group ID from collection name
            groupIdFromCollection = parseInt(inventoryState.collection.replace('shared_with_group_', ''));
            endpoint = `${API_BASE_URL}/api/sharing/items/my-items`;
          }
          // Note: System stock collection has been removed
          else if (inventoryState.collection.startsWith('shared-')) {
            // For shared collections, extract the collection ID
            const collectionId = inventoryState.collection.replace('shared-', '');
            endpoint = `${API_BASE_URL}/api/collections/${collectionId}/items`;
          } else {
            // For user collections, use the collection ID directly
            endpoint = `${API_BASE_URL}/api/collections/${inventoryState.collection}/items`;
          }
        } else {
          // For 'all' collection, use the regular items endpoint (only user's own items)
          endpoint = API_ENDPOINTS.GET_ITEMS;
        }
        
        // Add filters if specified
        let url = endpoint;
        const params = new URLSearchParams();
        
        // Add space_id filter to respect active hierarchical space
        if (activeSpace) {
          params.append('space_id', activeSpace.id.toString());
        }
        
        // Add group filter if specified (either from filterByGroupId prop or from collection name)
        const effectiveGroupId = groupIdFromCollection || filterByGroupId;
        if (effectiveGroupId) {
          params.append('group_id', effectiveGroupId.toString());
        }
        
        // Add access_type filter if specified (for "Shared With Me" pseudo-collection)
        if (accessTypeFilter) {
          params.append('access_type', accessTypeFilter);
        }
        
        // Append params to URL if any
        if (params.toString()) {
          url = `${endpoint}?${params.toString()}`;
        }
        
        console.log(`[InventoryView] Fetching items from: ${url}`);
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: abortController.signal,
        });
        
        console.log(`[InventoryView] Response status: ${response.status}`);
        if (!response.ok) throw new Error(`Failed to fetch items: ${response.status}`);
        
        const data = await response.json();
        // Handle both formats: {items: [...]} and direct array
        itemsArray = Array.isArray(data) ? data : (data.items || []);
        console.log(`[InventoryView] Fetched ${itemsArray.length} items`);
        
        // Note: allItemsCount is now managed by useInventoryCounts hook
      }
      
      // Only update state if data has changed (compare by stringifying)
      setItems(prevItems => {
        // For shopping list collections, clear any stale checked states
        // (shopping lists use checkbox tap as +1 qty, not check toggle)
        // Use ref to get current value, not stale closure
        const props = collectionPropertiesRef.current;
        if (props.collection_type === 'shopping' || props.collection_type === 'grocery') {
          itemsArray = itemsArray.map((item: any) => ({ ...item, is_checked: false }));
        }
        const newDataStr = JSON.stringify(itemsArray.map((item: any) => ({ 
          id: item.id, 
          name: item.name, 
          description: item.description,
          access_type: item.access_type,
          quantity: item.quantity,
          is_checked: item.is_checked,
          image_id: item.image_id,
          image_url: item.image_url,
          categories: item.categories,
          area: item.area,
          location: item.location,
          sub_location: item.sub_location,
          color: item.color,
          condition: item.condition,
          updated_at: item.updated_at,
          shared_with_count: item.shared_with_count
        })));
        const prevDataStr = JSON.stringify(prevItems.map(item => ({ 
          id: item.id, 
          name: item.name, 
          description: item.description,
          access_type: item.access_type,
          quantity: item.quantity,
          is_checked: item.is_checked,
          image_id: item.image_id,
          image_url: item.image_url,
          categories: item.categories,
          area: item.area,
          location: item.location,
          sub_location: (item as any).sub_location,
          color: item.color,
          condition: item.condition,
          updated_at: (item as any).updated_at,
          shared_with_count: (item as any).shared_with_count
        })));
        
        // Only update if data actually changed
        if (newDataStr !== prevDataStr) {
          // During silent refresh, keep existing items if new data is empty
          // This prevents flash of empty state during auto-refresh
          if (silent && itemsArray.length === 0 && prevItems.length > 0) {
            console.log('[InventoryView] Silent refresh returned empty, keeping existing items');
            return prevItems;
          }
          
          // Update lastItemsRef when we have new items (including empty array)
          // This ensures we clear the cache when collection becomes empty
          lastItemsRef.current = { items: itemsArray, collection: inventoryState.collection };
          return itemsArray;
        }
        return prevItems;
      });
      
      setError(null);
      recordNetworkSuccess(); // Network is working
      
      // Cache the fetched items
      setCachedItems(inventoryState.collection, itemsArray, activeSpace?.id);
      console.log(`[InventoryView] Cached ${itemsArray.length} items for collection ${inventoryState.collection} in space ${activeSpace?.id}`);
      
      // Validate against expected count and invalidate cache if mismatch
      const expectedCount = counts?.by_collection?.[inventoryState.collection];
      if (expectedCount !== undefined) {
        const isValid = validateItemsCount(inventoryState.collection, itemsArray, expectedCount);
        if (!isValid) {
          // Invalidate both caches to force fresh fetch on next access
          invalidateCollectionCache(inventoryState.collection);
          invalidateCountsCache();
        }
      }
      
      // If "Shared With Me" is selected and becomes empty, redirect to "All Items"
      if (inventoryState.collection === 'shared_with_me' && itemsArray.length === 0) {
        setCollection('all');
      }
      
      return itemsArray; // Return the items array for callers who need it
    } catch (err: any) {
      // Ignore AbortError - this is expected when switching collections
      if (err.name === 'AbortError') {
        return null;
      }
      
      // Retry logic for transient failures (up to 2 retries)
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 500; // 500ms, 1000ms
        console.log(`[InventoryView] Fetch failed, retrying in ${delay}ms (attempt ${retryCount + 1}/2)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchItems(silent, retryCount + 1);
      }
      
      // Track network failure for recovery (helps detect broken WebView state)
      recordNetworkFailure();
      
      // Only show error if not a silent refresh and all retries exhausted
      if (!silent) {
        setError('Failed to load items');
      }
      console.error('Fetch error after retries:', err);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [token, inventoryState.collection, activeSpace]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Scroll to newly matched items during voice search
  const voiceTermCountRef = useRef(0);
  const voicePrevItemIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isVoiceListening) {
      voiceTermCountRef.current = 0;
      voicePrevItemIdsRef.current = new Set();
      return;
    }
    const termCount = searchTerm.split(',').filter(t => t.trim()).length;
    if (termCount > voiceTermCountRef.current) {
      voiceTermCountRef.current = termCount;
      const timer = setTimeout(() => {
        const allItems = document.querySelectorAll('[data-item-id]');
        const currentIds = new Set<string>();
        let scrollTarget: Element | null = null;
        
        allItems.forEach(el => {
          const id = el.getAttribute('data-item-id') || '';
          currentIds.add(id);
          // Find first item that wasn't in previous set
          if (!scrollTarget && !voicePrevItemIdsRef.current.has(id)) {
            scrollTarget = el;
          }
        });
        
        if (scrollTarget) {
          (scrollTarget as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        voicePrevItemIdsRef.current = currentIds;
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, isVoiceListening]);

  // Scroll to newly added item after fetchItems re-renders the list
  useEffect(() => {
    if (scrollToItemId === null) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-item-id="${scrollToItemId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('item-highlight');
        setTimeout(() => el.classList.remove('item-highlight'), 2000);
      }
      setScrollToItemId(null);
    }, 300); // Wait for DOM to update after fetchItems
    return () => clearTimeout(timer);
  }, [scrollToItemId, items]);
  
  // Prefetch recently accessed collections in background
  useEffect(() => {
    if (token && counts) {
      // Delay prefetch to not interfere with initial load
      const timer = setTimeout(() => {
        prefetchRecentCollections(token, inventoryState.collection, API_BASE_URL);
      }, 2000); // 2 second delay after initial load
      
      return () => clearTimeout(timer);
    }
  }, [token, counts, inventoryState.collection]);
  
  // Load cached items immediately when collection changes, or clear if no cache
  useEffect(() => {
    const cached = getCachedItems(inventoryState.collection, activeSpace?.id);
    if (cached) {
      // Show cached items immediately for instant display
      setItems(cached);
      lastItemsRef.current = { items: cached, collection: inventoryState.collection };
      console.log(`[InventoryView] Loaded ${cached.length} cached items for collection ${inventoryState.collection}`);
    } else {
      // No cache, clear items
      setItems([]);
      lastItemsRef.current = { items: [], collection: inventoryState.collection };
    }
  }, [inventoryState.collection]);
  
  // Clear checked state when collection properties indicate shopping list
  // This handles the case where items load before properties are fetched
  useEffect(() => {
    if ((collectionProperties.collection_type === 'shopping' || collectionProperties.collection_type === 'grocery') && items.length > 0) {
      const hasCheckedItems = items.some(item => item.is_checked);
      if (hasCheckedItems) {
        setItems(prevItems => prevItems.map(item => ({ ...item, is_checked: false })));
      }
    }
  }, [collectionProperties.collection_type, items.length]);
  
  // Handle external collection navigation
  useEffect(() => {
    if (navigateToCollection !== undefined && navigateToCollection !== null) {
      setCollection(String(navigateToCollection));
    }
  }, [navigateToCollection, setCollection]);

  // Handle collection reset events from ModeContext
  useEffect(() => {
    const handleCollectionReset = (event: CustomEvent) => {
      const { collection } = event.detail;
      console.log('[InventoryView] Received collection reset event:', collection);
      setCollection(collection);
    };

    // Add event listener for collection reset
    window.addEventListener('collection-reset', handleCollectionReset as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('collection-reset', handleCollectionReset as EventListener);
    };
  }, [setCollection]);

  // When a lockedCollectionId is provided (Kitchen mode), force-select it
  const wasLockedRef = useRef(false);
  useEffect(() => {
    if (lockedCollectionId != null) {
      if (!wasLockedRef.current) {
        // Save current collection before locking (so we can restore it later)
        const current = localStorage.getItem('inventory-collection');
        if (current && current !== String(lockedCollectionId)) {
          localStorage.setItem('inventory-collection-before-lock', current);
        }
      }
      wasLockedRef.current = true;
      setCollection(String(lockedCollectionId));
    } else if (wasLockedRef.current && activeMode !== 'kitchen') {
      // Only restore when actually leaving kitchen mode, not during transient
      // null groceryCollectionId while switching spaces in kitchen mode
      wasLockedRef.current = false;
      setActiveRecipeName(null);
      setSearchTerm('');
      const saved = localStorage.getItem('inventory-collection-before-lock');
      setCollection(((saved || 'all') as any));
    } else if (wasLockedRef.current && activeMode === 'kitchen' && lockedCollectionId == null) {
      // Still in kitchen mode but grocery collection not ready (e.g., space switch)
      // Show All Items while waiting instead of a stale cross-space collection
      setCollection('all');
    }
  }, [lockedCollectionId, groceryCollectionId, activeMode]);

  // Activate a pending recipe (bindle search string) when switching from RecipesTab
  useEffect(() => {
    if (pendingRecipe) {
      setSearchTerm(pendingRecipe.search_string);
      setActiveRecipeName(pendingRecipe.name);
    }
  }, [pendingRecipe]);

  // Handle group filter navigation
  useEffect(() => {
    if (filterByGroupId !== undefined && filterByGroupId !== null) {
      // Set collection to special group filter format
      setCollection(`shared_with_group_${filterByGroupId}`);
      // Fetch items will automatically use the filterByGroupId
      fetchItems();
    }
  }, [filterByGroupId, setCollection, fetchItems]);
  
  // Refresh when refreshTrigger changes (silent refresh to avoid flash)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchItems(true); // silent=true to prevent loading flash
      // Also refresh collection counts since items were added/removed
      setCollectionRefreshTrigger(prev => prev + 1);
    }
  }, [refreshTrigger, fetchItems]);

  // Check for QR filter from scan page when component becomes active
  useEffect(() => {
    if (isActive) {
      const qrFilter = sessionStorage.getItem('qr_filter');
      if (qrFilter) {
        console.log('[InventoryView] Applying QR filter:', qrFilter);
        setSearchTerm(qrFilter);
        sessionStorage.removeItem('qr_filter'); // Clear after applying
      }
    }
  }, [isActive]);

  // Unified inventory synchronization system
  // Coordinates all refresh mechanisms for shared shopping lists:
  // - Periodic polling (45s) for shared updates
  // - App resume detection (after 30s background)
  // - Network recovery (auto-refresh when reconnected)
  // - Health checks (ensure collections never stay empty)
  useInventorySync({
    onRefreshItems: useCallback(async () => {
      await fetchItems(true);
    }, [fetchItems]),
    onRefreshCollections: useCallback(() => {
      // Trigger collection refresh by incrementing the trigger
      setCollectionRefreshTrigger(prev => prev + 1);
    }, []),
    onRefreshCounts: useCallback(async () => {
      await refetchCounts();
    }, [refetchCounts]),
    isActive,
    collectionsCount: counts?.by_collection ? Object.keys(counts.by_collection).length : 0,
    pollingInterval: 45000, // 45 seconds
  });

  // Immediate delete (no undo)
  const deleteNow = useCallback(async (baseId: number, scope: 'one' | 'image') => {
    if (!token) return;
    try {
      setSaving(true);
      const resp = await fetch(API_ENDPOINTS.DELETE_ITEM(baseId, scope), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Delete failed');
      
      // Invalidate cache for current collection and counts
      invalidateCollectionCache(inventoryState.collection);
      invalidateCountsCache();
      await refetchCounts();
      
      await fetchItems();
      // Trigger refresh to update nomads count and all items count
      setInternalRefreshTrigger(prev => prev + 1);
      // Refresh collection counts in selector
      setCollectionRefreshTrigger(prev => prev + 1);
      setEditOpen(false);
    } catch (e) {
      console.error('Delete failed', e);
      await fetchItems();
      setInternalRefreshTrigger(prev => prev + 1);
    } finally {
      setSaving(false);
    }
  }, [token, fetchItems, inventoryState.collection, refetchCounts]);

  // Handle QR code assignment
  const handleQRAssign = useCallback(async (qrCode: string, itemIds: number[]) => {
    if (!token) return;
    
    try {
      // Get full item objects for the selected items
      const selectedItems = items.filter(item => itemIds.includes(item.id));
      
      // Build items array with updated sub_location
      const itemsToUpdate = selectedItems.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        color: item.color || '',
        area: item.area || '',
        location: item.location || '',
        sub_location: qrCode, // Update sub_location to QR code
        quantity: item.quantity || 1,
        quantity_min: item.quantity_min || null,
        sale_price: item.sale_price || null,
        purchased_at: item.purchased_at || null,
        image_id: item.image_id || null,
        is_grocery: item.is_grocery,
        uit_id: item.uit_id,
        unit: item.unit,
        is_checked: item.is_checked
      }));

      // Bulk update sub_location for all selected items
      const response = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: itemsToUpdate
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update items');
      }

      // Refresh items to show updated sub_location
      await fetchItems();
      clearSelection();
      setMode('view');
    } catch (error) {
      console.error('[InventoryView] Failed to assign QR code:', error);
      throw error;
    }
  }, [token, items, fetchItems, clearSelection, setMode]);

  // Memoized callbacks for MobileItemEdit to prevent remounting
  const handleMobileClose = useCallback(() => {
    setEditOpen(false);
    setIsLocked(true);
  }, []);
  
  const handleMobileChange = useCallback((patch: Partial<EditableItem>) => {
    updateEditItem(patch);
  }, [updateEditItem]);
  
  const handleMobileCloseCard = useCallback(() => {
    // hasChanges will be calculated in the component where this is called
    const originalItem = items.find(i => i.id === editItem?.id);
    const hasChanges = originalItem && editItem ? (
      String(editItem.name || '') !== String(originalItem.name || '') ||
      String(editItem.description || '') !== String(originalItem.description || '') ||
      JSON.stringify(editItem.categories?.map(c => c.id).sort() || []) !== JSON.stringify(originalItem.categories?.map(c => c.id).sort() || []) ||
      String(editItem.color || '') !== String(originalItem.color || '') ||
      String(editItem.area || '') !== String(originalItem.area || '') ||
      String(editItem.location || '') !== String(originalItem.location || '')
    ) : false;
    
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Close anyway?')) {
        setEditOpen(false);
        setIsLocked(true);
      }
    } else {
      setEditOpen(false);
      setIsLocked(true);
    }
  }, [items, editItem]);
  
  const handleMobileDelete = useCallback(async (scope: 'one' | 'image') => {
    if (!token || !editItem) return;
    await deleteNow(editItem.id, scope);
  }, [token, editItem, deleteNow]);
  
  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModalTrigger(false);
  }, []);
  
  const handleMobileShowAll = useCallback(() => {
    if (editImageId) {
      setImageFilterId(editImageId);
    }
    setEditOpen(false);
    setIsLocked(true);
  }, [editImageId]);
  
  // Handler to open manage sharing modal for a specific item
  const openManageSharingForItem = useCallback((itemId: number, itemName?: string) => {
    selectAllItems([itemId]);
    setManageSharingItemName(itemName);
    setShowManageSharingModal(true);
  }, [selectAllItems]);
  
  const handleMobileShare = useCallback(() => {
    if (!editItem) return;
    openManageSharingForItem(editItem.id, editItem.name);
  }, [editItem, openManageSharingForItem]);
  
  const handleShareBadgeClick = useCallback((item: SavedItem) => {
    openManageSharingForItem(item.id, item.name);
  }, [openManageSharingForItem]);
  
  // Handler for direct share (now uses unified modal)
  const handleDirectShare = useCallback(() => {
    if (!editItem) return;
    openManageSharingForItem(editItem.id, editItem.name);
  }, [editItem, openManageSharingForItem]);

  // Duplicate item handlers
  const handleDuplicateKeep = useCallback(async (itemId: number) => {
    if (!token) return;
    try {
      // If in a collection and item not already in it, add it
      const collId = inventoryState.collection !== 'all' ? collectionProperties.id : undefined;
      const alreadyIn = collId && duplicateDialogInfo?.duplicate_collection_ids.some(id => Number(id) === Number(collId));
      if (collId && !alreadyIn) {
        await fetch(API_ENDPOINTS.ADD_ITEMS_TO_COLLECTION(collId), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_ids: [itemId] }),
        });
      }
      fetchItems();
      setShowAddItemModal(false);
      closeEdit();
    } catch (err) {
      console.error('Failed to keep existing item:', err);
    }
  }, [token, fetchItems, closeEdit, inventoryState.collection, collectionProperties.id, duplicateDialogInfo]);

  const handleDuplicateReplace = useCallback(async (itemId: number) => {
    if (!token || !duplicateDialogInfo?.newItemData) return;
    try {
      const newData = duplicateDialogInfo.newItemData;
      // Update the existing item with the new data
      const updatePayload: any = {
        id: itemId,
        name: newData.name,
      };
      if (newData.description !== undefined) updatePayload.description = newData.description;
      if (newData.quantity !== undefined) updatePayload.quantity = newData.quantity;
      if (newData.color !== undefined) updatePayload.color = newData.color;
      if (newData.category_ids !== undefined) updatePayload.category_ids = newData.category_ids;
      if (newData.image_id !== undefined) updatePayload.image_id = newData.image_id;

      const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [updatePayload] }),
      });

      // If in a collection and item not already in it, add it
      if (resp.ok) {
        const collId = inventoryState.collection !== 'all' ? collectionProperties.id : undefined;
        const alreadyIn = collId && duplicateDialogInfo.duplicate_collection_ids.some(id => Number(id) === Number(collId));
        if (collId && !alreadyIn) {
          await fetch(API_ENDPOINTS.ADD_ITEMS_TO_COLLECTION(collId), {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ item_ids: [itemId] }),
          });
        }
        await fetchItems();
        setScrollToItemId(itemId);
        setShowAddItemModal(false);
        closeEdit();
      }
    } catch (err) {
      console.error('Failed to replace item:', err);
    }
  }, [token, fetchItems, closeEdit, inventoryState.collection, collectionProperties.id, duplicateDialogInfo]);

  const handleDuplicateIncreaseQuantity = useCallback(async (itemId: number) => {
    if (!token || !duplicateDialogInfo) return;
    try {
      const newQty = (duplicateDialogInfo.duplicate_item_quantity ?? 1) + 1;
      const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: itemId, quantity: newQty }] }),
      });
      if (resp.ok) {
        await fetchItems();
        setScrollToItemId(itemId);
        setShowAddItemModal(false);
        closeEdit();
      }
    } catch (err) {
      console.error('Failed to increase item quantity:', err);
    }
  }, [token, fetchItems, closeEdit, duplicateDialogInfo]);

  // Replace photo handler
  const handleReplacePhoto = useCallback((scope: 'one' | 'all') => {
    setReplacePhotoScope(scope);
    
    // Calculate item count based on scope
    if (scope === 'all' && editItem) {
      const oldImageId = items.find(i => i.id === editItem.id)?.image_id;
      if (oldImageId) {
        const count = items.filter(i => i.image_id === oldImageId).length;
        setReplacePhotoItemCount(count);
      } else {
        setReplacePhotoItemCount(1);
      }
    } else {
      setReplacePhotoItemCount(1);
    }
    
    // Show the photo replacement modal
    setShowPhotoReplacementModal(true);
  }, [editItem, items]);

  // Handle photo selection from modal
  const handlePhotoSelected = useCallback(async (file: File, scope: 'one' | 'all') => {
    if (!token || !editItem) return;

    try {
      setSaving(true);
      setReplacePhotoScope(scope);
      
      // Upload new image
      const formData = new FormData();
      formData.append('image', file);
      formData.append('create_image', 'true');
      formData.append('upload_only', 'true'); // Skip analysis, just upload
      
      const uploadResp = await fetch(API_ENDPOINTS.ANALYZE, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      let newImageId: number | undefined;
      if (uploadResp.status === 409) {
        // Image already exists with items - extract the image_id from the response
        const conflictData = await uploadResp.json();
        newImageId = conflictData.image_id;
      } else if (!uploadResp.ok) {
        throw new Error('Image upload failed');
      } else {
        const data = await uploadResp.json();
        newImageId = data.image_id || data.image?.id;
      }
      
      if (!newImageId) {
        throw new Error('No image_id received from upload');
      }
      
      // Update item(s) with new image_id
      if (scope === 'one') {
        // Update only this item
        const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: editItem.id,
              name: editItem.name,
              description: editItem.description,
              category_ids: editItem.categories?.map(c => c.id) || [],
              color: editItem.color,
              area: editItem.area,
              location: editItem.location,
              sub_location: editItem.sub_location,
              quantity: editItem.quantity,
              quantity_min: editItem.quantity_min,
              image_id: newImageId,
            }],
          }),
        });
        if (!resp.ok) throw new Error('Update failed');
      } else {
        // Update all items with the same image_id
        const oldImageId = items.find(i => i.id === editItem.id)?.image_id;
        if (oldImageId) {
          const itemsToUpdate = items.filter(i => i.image_id === oldImageId);
          const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: itemsToUpdate.map(item => ({
                id: item.id,
                name: item.name,
                description: item.description,
                category_ids: item.categories?.map(c => c.id) || [],
                color: item.color,
                area: item.area,
                location: item.location,
                image_id: newImageId,
              })),
            }),
          });
          if (!resp.ok) throw new Error('Batch update failed');
        }
      }
      
      // Refresh items to show new image
      await fetchItems();
      
      // Close the edit modal to force a fresh render with new image
      setEditOpen(false);
      
    } catch (e) {
      console.error('Replace photo failed', e);
      alert('Failed to replace photo. Please try again.');
    } finally {
      setSaving(false);
      setReplacePhotoScope(null);
      setShowPhotoReplacementModal(false);
    }
  }, [token, editItem, items, fetchItems]);

  // Fetch Unsplash images when editItem is set (not when modal opens) to prevent flash
  useEffect(() => {
    if (!editItem?.name) {
      setEditModalSearchImages([]);
      setImageSearchLoading(false);
      setImageSearchPage(1);
      setImageSearchTermOverride('');
      return;
    }
    setImageSearchPage(1);
    setImageSearchTermOverride('');

    // Check cache first (memory + localStorage)
    const cached = getCachedImages(editItem.name);
    const cachedPhotos = getCachedPhotoData(editItem.name);
    
    // Only use cache if we have BOTH URLs and photo data
    // If we only have URLs (old cache), re-fetch to get metadata
    if (cached && cached.length > 0 && cachedPhotos && cachedPhotos.length > 0) {
      setEditModalSearchImages(cached);
      setEditModalPhotoData(cachedPhotos);
      setImageSearchLoading(false);
      return;
    }

    // Fetch from Unsplash API
    let isCancelled = false;
    setImageSearchLoading(true);
    
    const fetchWikimediaImages = async () => {
      try {
        const searchTerm = editItem.name.trim();
        
        // Using Unsplash API
        const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
        if (!accessKey) {
          console.error('Unsplash API key not configured');
          return;
        }
        
        const { buildUnsplashSearchUrl } = await import('../utils/unsplashUtils');
        const searchUrl = buildUnsplashSearchUrl(searchTerm, accessKey);
        
        const response = await fetch(searchUrl);
        if (!response.ok) {
          console.error('Unsplash API error:', response.status, response.statusText);
          throw new Error('Unsplash search failed');
        }
        
        const data = await response.json();
        
        if (!isCancelled) {
          if (data.results && data.results.length > 0) {
            // Extract small thumbnail URLs (400px width) for backward compatibility
            const imageUrls = data.results.map((photo: any) => photo.urls.small);
            
            // Cache both URLs and photo data (memory + localStorage)
            setCachedImages(editItem.name, imageUrls);
            setCachedPhotoData(editItem.name, data.results);
            
            // Store both URLs and full photo data in state
            setEditModalSearchImages(imageUrls);
            setEditModalPhotoData(data.results); // Store full photo objects with metadata
          }
          setImageSearchLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to fetch Unsplash images:', error);
          setImageSearchLoading(false);
        }
      }
    };

    // Debounce the fetch slightly to avoid rapid requests
    const timer = setTimeout(fetchWikimediaImages, 100); // Reduced from 300ms for faster modal response

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [editItem?.name]);

  const filteredItems = React.useMemo(() => {
    // Items are already filtered by collection from the API
    // Just apply search and other filters
    // Parse search into OR groups of AND terms (comma = OR, + = AND)
    const searchGroups = parseSearchGroups(searchTerm);

    const filtered = items.filter(item => {
      // OR across groups, AND within each group
      const matchesSearch = itemMatchesSearch(searchGroups, item, isVoiceListening);
      // Old category filter is deprecated - use selectedCategoryIds instead
      const matchesCategory = selectedCategory === 'all' || 
        (item.categories && item.categories.some(cat => cat.label === selectedCategory));
      
      // New multi-category filter: item must have at least one of the selected categories
      const matchesNewCategories = selectedCategoryIds.length === 0 || 
        (item.categories && item.categories.some(cat => selectedCategoryIds.includes(cat.id)));
      
      const matchesImage = imageFilterId == null || item.image_id === imageFilterId;
      
      // Location filter logic
      let matchesLocation = true;
      if (selectedLocations.length > 0) {
        // Check if item's area:location combination is in selected locations
        matchesLocation = selectedLocations.some(locKey => {
          const [area, location] = locKey.split(':');
          return item.area === area && item.location === location;
        });
      } else if (selectedAreas.length > 0) {
        // If only areas selected (no specific locations), match by area
        matchesLocation = selectedAreas.includes(item.area);
      }
      
      // Stock filter logic
      let matchesStock = true;
      if (stockFilter === 'low') {
        // Low stock: quantity <= min_quantity
        matchesStock = (item.quantity !== undefined && item.quantity_min !== undefined) &&
                      item.quantity <= item.quantity_min;
      } else if (stockFilter === 'out') {
        // Out of stock: quantity = 0 (check actual value, not default)
        matchesStock = item.quantity === 0;
      }
      
      // Shopping list filter: when "0 only" is checked, only show items with quantity 0
      let matchesStockChecklist = true;
      if (viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop' && showAllStockChecklistItems) {
        matchesStockChecklist = (item.quantity ?? 1) === 0;
      }

      // Date filter (match by LOCAL calendar date portion of created_at, updated_at, or purchased_at);
      // only active when dateFilter is non-empty. Using local time avoids
      // off-by-one issues when the backend stores timestamps in UTC.
      let matchesDate = true;
      if (dateFilter) {
        try {
          let dateToCheck;
          if (dateFieldType === 'created') {
            dateToCheck = item.created_at;
          } else if (dateFieldType === 'updated') {
            dateToCheck = item.updated_at || item.created_at;
          } else if (dateFieldType === 'purchased') {
            // purchased_at can be null - if null, item doesn't match
            if (!item.purchased_at) {
              matchesDate = false;
            } else {
              dateToCheck = item.purchased_at;
            }
          }
          
          if (matchesDate && dateToCheck) {
            const d = new Date(dateToCheck);
            const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            matchesDate = itemDate === dateFilter;
          }
        } catch {
          matchesDate = true;
        }
      }
      
      // In Shop mode with bindle active, bypass search filter entirely — bindle only controls
      // the orange dot indicator on checkboxes, not which items are shown
      const isShopWithBindle = viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop' && activeBindle;
      const effectiveMatchesSearch = isShopWithBindle || matchesSearch;

      return effectiveMatchesSearch && matchesCategory && matchesNewCategories && matchesImage && matchesLocation && matchesStock && matchesStockChecklist && matchesDate;
    });

    // Sort items
    const sorted = [...filtered].sort((a, b) => {
      // Special sorting for shopping list collections when sortBy is 'shopping-list'
      // Priority: qty 0 (need to buy), then 1+ (have), then -1 (don't want), then category, then name
      if (sortBy === 'shopping-list') {
        const aQty = a.quantity ?? 0;
        const bQty = b.quantity ?? 0;
        
        // Define sort order: 0 first, then positive, then -1
        const getQuantityPriority = (qty: number) => {
          if (qty === 0) return 0;      // Need to buy - highest priority
          if (qty > 0) return 1;         // Have in stock - medium priority
          return 2;                      // Don't want (-1) - lowest priority
        };
        
        const aPriority = getQuantityPriority(aQty);
        const bPriority = getQuantityPriority(bQty);
        
        // First sort by quantity priority (always same order)
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // Then by category (respects sortDirection)
        const aCategory = a.categories?.[0]?.label || '';
        const bCategory = b.categories?.[0]?.label || '';
        const categoryCompare = sortDirection === 'asc' 
          ? aCategory.localeCompare(bCategory)
          : bCategory.localeCompare(aCategory);
        if (categoryCompare !== 0) {
          return categoryCompare;
        }
        
        // Finally by name (respects sortDirection)
        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      
      // Regular sorting for non-shopping-list collections
      let compareValue = 0;
      
      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'category':
          // Sort by first category label, or empty string if no categories
          const aCategory = a.categories?.[0]?.label || '';
          const bCategory = b.categories?.[0]?.label || '';
          compareValue = aCategory.localeCompare(bCategory);
          break;
        case 'location':
          const aLoc = `${a.area} ${a.location}`.trim();
          const bLoc = `${b.area} ${b.location}`.trim();
          compareValue = aLoc.localeCompare(bLoc);
          break;
        case 'quantity':
          // Sort by quantity, treating undefined as 1 (default value)
          const aQty = a.quantity ?? 1;
          const bQty = b.quantity ?? 1;
          compareValue = aQty - bQty;
          break;
        case 'created':
          compareValue = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'updated':
          // Assuming items have updated_at, otherwise use created_at
          const aDate = (a as any).updated_at || a.created_at;
          const bDate = (b as any).updated_at || b.created_at;
          compareValue = new Date(aDate).getTime() - new Date(bDate).getTime();
          break;
      }
      
      // Apply primary sort direction
      const primaryResult = sortDirection === 'asc' ? compareValue : -compareValue;
      
      // If primary sort is not 'name' and values are equal, use name as secondary sort (always ascending)
      if (primaryResult === 0 && sortBy !== 'name') {
        return a.name.localeCompare(b.name);
      }
      
      return primaryResult;
    });

    return sorted;
  }, [items, inventoryState.collection, searchTerm, selectedCategory, selectedCategoryIds, imageFilterId, selectedAreas, selectedLocations, stockFilter, sortBy, sortDirection, viewMode, showAllStockChecklistItems, stockChecklistMode, dateFilter, dateFieldType, activeBindle, isVoiceListening]);

  // Compute unmatched search terms (terms that don't match any item name)
  // Used for the "Add Missing" feature
  const unmatchedTerms = React.useMemo(() => {
    if (!searchTerm.trim()) return [];
    const groups = parseSearchGroups(searchTerm);
    // Flatten all terms from all groups, keep only plain name terms (no #, @, ~ prefixes)
    const nameTerms: ParsedTerm[] = [];
    for (const group of groups) {
      for (const pt of group) {
        if (!pt.term.startsWith('#') && !pt.term.startsWith('@') && !pt.term.startsWith('~')) {
          nameTerms.push(pt);
        }
      }
    }
    if (nameTerms.length === 0) return [];
    
    // Helper function to normalize names for comparison
    const normalizeForMatch = (name: string): string => {
      return name.toLowerCase()
        .replace(/\b\d+(?:\.\d+)?\s*(?:kg|g|lb|oz|lbs|grams?|ounces?|pounds?)\b/g, '')
        .replace(/\b(weighing|approximately|about|around|roughly)\b/g, '')
        .replace(/ies\b/g, 'y')
        .replace(/s\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    return nameTerms
      .filter(pt => {
        if (!pt.term) return false;
        const normalizedSearchTerm = normalizeForMatch(pt.term);
        return !items.some(item => {
          const normalizedItemName = normalizeForMatch(item.name || '');
          if (pt.isExact) {
            return normalizedItemName === normalizedSearchTerm;
          }
          return normalizedItemName.includes(normalizedSearchTerm);
        });
      })
      .map(pt => pt.raw);
  }, [searchTerm, items]);

  // Auto-open modal when recipe is activated and there are missing items
  React.useEffect(() => {
    if (pendingRecipe && unmatchedTerms.length > 0 && !showAddMissingModal) {
      setShowAddMissingModal(true);
    }
  }, [pendingRecipe, unmatchedTerms.length, showAddMissingModal]);

  // Compute set of item IDs that match the active bindle's search terms
  // Used to show a circle indicator in checkboxes for bindle items in Shop mode
  const bindleItemIds = useMemo(() => {
    if (!activeBindle || !searchTerm) return undefined;
    const groups = parseSearchGroups(searchTerm);
    if (groups.length === 0) return undefined;

    const ids = new Set<number>();
    for (const item of items) {
      if (itemMatchesSearch(groups, item)) ids.add(item.id);
    }
    return ids;
  }, [items, activeBindle, searchTerm]);

  // Detect missing bindle terms — compare search term count vs matched item count
  const missingBindleTerms = useMemo(() => {
    if (!activeBindle || !searchTerm || !bindleItemIds) return [];
    const groups = parseSearchGroups(searchTerm);
    // Flatten all terms for counting
    const allTerms = groups.flatMap(g => g.map(pt => pt.raw));
    if (allTerms.length === 0) return [];
    // If matched items >= search terms, nothing is missing
    if (bindleItemIds.size >= allTerms.length) return [];
    // Find which terms don't have a matching item (by exact name)
    const itemNames = new Set(items.map(item => (item.name || '').toLowerCase().trim()));
    return allTerms.filter(term => !itemNames.has(term.toLowerCase()));
  }, [items, activeBindle, searchTerm, bindleItemIds]);

  // Apply pending quantity changes to display items (for Manage mode)
  const displayItems = useMemo(() => {
    if (Object.keys(pendingQuantityChanges).length === 0) {
      return filteredItems;
    }
    
    return filteredItems.map(item => {
      const pendingQty = pendingQuantityChanges[item.id];
      if (pendingQty !== undefined) {
        return { ...item, quantity: pendingQty };
      }
      return item;
    });
  }, [filteredItems, pendingQuantityChanges]);

  // Helper to extract numeric collection ID from various formats
  const getNumericCollectionId = useCallback((collection: string): string | null => {
    if (collection.startsWith('shared-')) {
      return collection.replace('shared-', '');
    } else if (collection.startsWith('merged-')) {
      const ids = collection.replace('merged-', '').split('-');
      return ids[0];
    } else if (!isNaN(Number(collection))) {
      return collection;
    }
    return null;
  }, []);

  // Voice command callbacks - must be after displayItems is defined
  const handleCheckAll = useCallback(async () => {
    if (!token || !inventoryState.collection || inventoryState.collection === 'all') return;
    
    const collectionId = getNumericCollectionId(inventoryState.collection);
    if (!collectionId) return;
    
    // Fetch current items fresh to avoid stale closure
    const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/items`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    const currentItems = Array.isArray(data) ? data : (data.items || []);
    
    const uncheckedItems = currentItems.filter((i: any) => !i.is_checked);
    for (const item of uncheckedItems) {
      try {
        await fetch(`${API_BASE_URL}/api/collections/${collectionId}/items/${item.id}/check`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_checked: true })
        });
      } catch (error) {
        console.error('Failed to check item:', error);
      }
    }
    await fetchItems();
  }, [token, inventoryState.collection, fetchItems, getNumericCollectionId]);

  const handleUncheckAll = useCallback(async () => {
    if (!token || !inventoryState.collection || inventoryState.collection === 'all') return;
    
    const collectionId = getNumericCollectionId(inventoryState.collection);
    if (!collectionId) return;
    
    // Fetch current items fresh to avoid stale closure
    const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/items`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    const currentItems = Array.isArray(data) ? data : (data.items || []);
    
    const checkedItems = currentItems.filter((i: any) => i.is_checked);
    
    for (const item of checkedItems) {
      try {
        await fetch(`${API_BASE_URL}/api/collections/${collectionId}/items/${item.id}/check`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_checked: false })
        });
      } catch (error) {
        console.error('Failed to uncheck item:', error);
      }
    }
    await fetchItems();
  }, [token, inventoryState.collection, fetchItems, getNumericCollectionId]);

  // Update counts whenever items, filteredItems, selectedIds, or mode change
  useEffect(() => {
    if (onInventoryCountChange) {
      // Count shared items (items with access_type that are not 'my_personal')
      const sharedCount = items.filter(item => 
        item.access_type && item.access_type !== 'my_personal'
      ).length;
      // Count checked items (items with is_checked = true)
      const completedCount = items.filter(item => item.is_checked).length;
      const isSelectMode = inventoryState.mode === 'manage';
      onInventoryCountChange(items.length, filteredItems.length, selectedIds.size, sharedCount, completedCount, isSelectMode);
    }
  }, [items.length, filteredItems.length, selectedIds.size, inventoryState.mode, onInventoryCountChange, items]);

  // Add/remove CSS class for 2-item case to adjust padding
  useEffect(() => {
    const itemsGrid = document.querySelector('.items-grid');
    if (itemsGrid) {
      if (filteredItems.length === 2) {
        itemsGrid.classList.add('two-items');
      } else {
        itemsGrid.classList.remove('two-items');
      }
    }
  }, [filteredItems.length]);

  // Extract unique category labels from all items (old category system - deprecated)
  const categories = ['all', ...Array.from(new Set(
    items.flatMap(item => item.categories?.map(c => c.label).filter((label): label is string => label !== undefined) || [])
  )).sort()]

  // Format label for active image filter: use the creation date of the first item in that image group
  const formatImageGroupDate = (imgId: number | null): string | null => {
    if (imgId == null) return null;
    const group = items.filter(i => i.image_id === imgId);
    if (!group.length) return null;
    const created = group[0]?.created_at;
    if (!created) return null;
    const d = new Date(created);
    if (isNaN(d.getTime())) return null;
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const pad = (n: number) => String(n).padStart(2, '0');
    const h = d.getHours();
    const h12 = ((h + 11) % 12) + 1;
    const ap = h >= 12 ? 'PM' : 'AM';
    return `${days[d.getDay()]}, ${pad(d.getMonth()+1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(h12)}:${pad(d.getMinutes())} ${ap}`;
  };

  // OLD MOBILE EDIT VIEW REMOVED - Now using UnifiedItemEditModal for all modes
  // The UnifiedItemEditModal handles mobile/desktop/guest automatically

  // Collection modal handlers (moved from UnifiedInventoryMenuBar)
  const handleShowCollectionModal = (id?: number, name?: string, _itemCount?: number) => {
    setSelectedCollectionId(id);
    setSelectedCollectionName(name || '');
    setShowCollectionModal(true);
  };

  const handleEmptyCollection = async (id: number, name: string) => {
    if (!window.confirm(`Remove all items from "${name}"? The items will not be deleted, only removed from this collection.`)) {
      return;
    }
    
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/collections/${id}/items`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setCollectionRefreshTrigger(prev => prev + 1);
        fetchItems();
        window.dispatchEvent(new CustomEvent('collections-updated'));
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to empty collection');
      }
    } catch (error) {
      console.error('Error emptying collection:', error);
      alert('Failed to empty collection');
    }
  };

  const handleDeleteCollection = async (id: number, name: string) => {
    if (!window.confirm(`Delete collection "${name}"? This will not delete the items.`)) {
      return;
    }
    
    if (!token) return;

    try {
      const response = await fetch(API_ENDPOINTS.DELETE_COLLECTION(id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setCollectionRefreshTrigger(prev => prev + 1);
        fetchItems();
        window.dispatchEvent(new CustomEvent('collections-updated'));
        // If we're viewing the deleted collection, switch to "all"
        if (inventoryState.collection === id.toString()) {
          setCollection('all');
        }
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert('Failed to delete collection');
    }
  };

  // Check if sharing modal is open
  const isSharingModalOpen = showManageSharingModal;
  
  // In Capacitor, always show menu bar (don't hide for modals)
  const isCapacitor = document.documentElement.classList.contains('capacitor-app');
  const shouldShowInputArea = isCapacitor ? true : !isSharingModalOpen;

  // Extract menu bar for UnifiedHeader
  const inventoryMenuBar = shouldShowInputArea ? (
    <UnifiedInventoryMenuBar
          collection={inventoryState.collection}
          onCollectionChange={setCollection}
          collectionCounts={itemCounts}
          onCollectionsRefresh={() => {
            fetchItems();
            setInternalRefreshTrigger(prev => prev + 1);
          }}
          isSelectMode={isSelectMode}
          onSelectModeToggle={() => {
            if (isSelectMode) {
              exitSelectMode();
              setMode('view');
            } else {
              enterSelectMode();
              setMode('manage');
            }
          }}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onVoiceListeningChange={onVoiceListeningChange}
          filteredItemsCount={filteredItems.length}
          collectionId={collectionProperties.id}
          collectionPermission={collectionProperties.permission}
          onActiveBindleChange={(bindle) => {
            setActiveBindle(bindle);
            if (!bindle) setBindleDescExpanded(false);
          }}
          onFilterClick={() => setShowSearchModal(true)}
          onDisplayClick={() => setShowDisplayModal(true)}
          hasActiveFilters={
            searchTerm !== '' ||
            selectedCategory !== 'all' ||
            selectedCategoryIds.length > 0 ||
            selectedAreas.length > 0 ||
            selectedLocations.length > 0 ||
            stockFilter !== 'off' ||
            imageFilterId !== null ||
            dateFilter !== ''
          }
          selectedCount={selectedIds.size}
          selectedItems={Array.from(selectedIds)}
          onSelectAll={() => {
            selectAll(displayItems);
          }}
          onClearSelection={clearSelection}
          items={displayItems.map(item => ({ id: item.id, name: item.name, checked: item.is_checked }))}
          isChecklistMode={collectionProperties.collection_type === 'checklist' || collectionProperties.collection_type === 'shopping' || collectionProperties.collection_type === 'grocery'}
          isStockKeeping={collectionProperties.collection_type === 'stock' || collectionProperties.collection_type === 'shopping' || collectionProperties.collection_type === 'grocery'}
          onCheckAll={handleCheckAll}
          onUncheckAll={handleUncheckAll}
          onRegisterVoiceToggle={(fn) => { voiceToggleRef.current = fn; }}
          onToggleItem={async (itemId) => {
            const item = items.find(i => i.id === itemId);
            if (!item || !token || !inventoryState.collection || inventoryState.collection === 'all') return;
            
            // Extract numeric collection ID from various formats
            let collectionIdForToggle: string | null = null;
            if (inventoryState.collection.startsWith('shared-')) {
              collectionIdForToggle = inventoryState.collection.replace('shared-', '');
            } else if (inventoryState.collection.startsWith('merged-')) {
              const ids = inventoryState.collection.replace('merged-', '').split('-');
              collectionIdForToggle = ids[0];
            } else if (!isNaN(Number(inventoryState.collection))) {
              collectionIdForToggle = inventoryState.collection;
            }
            
            if (!collectionIdForToggle) return;
            
            const originalItems = [...items];
            const previousQuantity = item.quantity ?? 0;
            
            // Stock-checklist Shop mode: checking = increment qty
            if (viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop') {
              if (!item.is_checked) {
                const newQuantity = previousQuantity + 1;
                const now = new Date().toISOString();
                
                // Optimistically update UI with purchased_at
                setItems(prevItems => 
                  prevItems.map(i => 
                    i.id === itemId ? { ...i, quantity: newQuantity, purchased_at: now } : i
                  )
                );
                
                try {
                  const updatePayload: any = {
                    id: itemId,
                    name: item.name,
                    quantity: newQuantity,
                    purchased_at: now
                  };
                  
                  const updateResp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      items: [updatePayload]
                    })
                  });
                  
                  if (!updateResp.ok) {
                    console.error('Failed to update quantity');
                    setItems(originalItems);
                    return;
                  }
                  
                  showUndoWithTimer({
                    type: 'quantity',
                    itemId,
                    previousValue: previousQuantity,
                    newValue: newQuantity,
                    timestamp: Date.now(),
                  });
                } catch (error) {
                  console.error('Error updating quantity:', error);
                  setItems(originalItems);
                }
              }
              return;
            }
            
            // Regular checklist mode: just toggle check state
            const newCheckedState = !item.is_checked;
            const originalItemsForCheck = [...items];
            
            setItems(prevItems => 
              prevItems.map(i => 
                i.id === itemId ? { ...i, is_checked: newCheckedState } : i
              )
            );
            
            try {
              const response = await fetch(`${API_BASE_URL}/api/collections/${collectionIdForToggle}/items/${itemId}/check`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_checked: newCheckedState })
              });
              
              if (!response.ok) {
                console.error('Failed to toggle item:', response.status);
                setItems(originalItemsForCheck);
              }
            } catch (error) {
              console.error('Failed to toggle checked state:', error);
              setItems(originalItemsForCheck);
            }
          }}
          onAction={async (action, itemIds) => {
              // Handle bulk actions from Manage dropdown
              switch (action) {
                case 'copy-from-template':
                  break;
                  
                case 'add-to-collection':
                  setShowAddToCollectionModal(true);
                  break;

                case 'move-to-collection':
                  setCollectionActionMode('move');
                  setShowAddToCollectionModal(true);
                  break;
                  
                case 'remove-from-collection':
                  // Remove selected items from current collection
                  const collectionId = collectionProperties.id;
                  if (!collectionId) {
                    alert('Cannot remove items: not viewing a specific collection');
                    return;
                  }
                  
                  const collectionDisplayName = collectionName || 'this collection';
                  if (!window.confirm(`Remove ${itemIds.length} item${itemIds.length > 1 ? 's' : ''} from ${collectionDisplayName}?`)) {
                    return;
                  }
                  
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    
                    for (const itemId of itemIds) {
                      try {
                        const response = await fetch(
                          API_ENDPOINTS.REMOVE_ITEM_FROM_COLLECTION(collectionId, itemId),
                          {
                            method: 'DELETE',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                            },
                          }
                        );
                        
                        if (response.ok) {
                          successCount++;
                        } else {
                          errorCount++;
                          console.error(`Failed to remove item ${itemId}`);
                        }
                      } catch (e) {
                        errorCount++;
                        console.error(`Error removing item ${itemId}:`, e);
                      }
                    }
                    
                    if (successCount > 0) {
                      // Refresh items to reflect changes
                      await fetchItems();
                      // Refresh collection counts since items were removed
                      setCollectionRefreshTrigger(prev => prev + 1);
                      clearSelectionState();
                      setMode('view');
                    }
                    
                    if (errorCount > 0) {
                      alert(`Removed ${successCount} items. ${errorCount} items failed to remove.`);
                    }
                  } catch (e) {
                    console.error('Bulk remove from collection error:', e);
                    alert('Failed to remove items from collection');
                  }
                  break;
                  
                case 'delete':
                  // Get selected items
                  const selectedItemsToDelete = items.filter(item => itemIds.includes(item.id));
                  
                  if (selectedItemsToDelete.length === 0) {
                    alert('No items selected for deletion.');
                    return;
                  }
                  
                  // Check if viewing a specific collection (not "All Items")
                  const isInCollection = collectionProperties.id && inventoryState.collection !== 'all';
                  
                  // Separate items by ownership
                  // Note: If user_id is undefined, treat as owned by current user (legacy items or personal items)
                  const ownedByUser = selectedItemsToDelete.filter(item => 
                    !item.user_id || item.user_id === user?.id
                  );
                  const ownedByOthers = selectedItemsToDelete.filter(item => 
                    item.user_id && item.user_id !== user?.id
                  );
                  
                  // Build confirmation message
                  let confirmMsg = '';
                  if (isInCollection && ownedByOthers.length > 0) {
                    // In a collection with items owned by others
                    if (ownedByUser.length > 0) {
                      confirmMsg = `Permanently delete ${ownedByUser.length} of your items and remove ${ownedByOthers.length} items added by others from this collection?`;
                    } else {
                      confirmMsg = `Remove ${ownedByOthers.length} item${ownedByOthers.length > 1 ? 's' : ''} from this collection?`;
                    }
                  } else if (ownedByOthers.length > 0) {
                    // Not in a collection, but trying to delete others' items
                    alert(`Cannot delete: ${ownedByOthers.length} item${ownedByOthers.length > 1 ? 's are' : ' is'} not owned by you.`);
                    return;
                  } else {
                    // All items owned by user
                    confirmMsg = `Permanently delete ${selectedItemsToDelete.length} item${selectedItemsToDelete.length > 1 ? 's' : ''}?\n\n(This will move ${selectedItemsToDelete.length > 1 ? 'them' : 'it'} to trash)`;
                  }
                  
                  if (!window.confirm(confirmMsg)) return;
                  
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    
                    for (const item of selectedItemsToDelete) {
                      try {
                        await deleteNow(item.id, 'one');
                        successCount++;
                      } catch (e) {
                        console.error(`Failed to delete item ${item.id}:`, e);
                        errorCount++;
                      }
                    }
                    
                    if (errorCount > 0) {
                      alert(`Deleted ${successCount} items. ${errorCount} items failed to delete.`);
                    }
                    
                    clearSelectionState();
                    setMode('view'); // Exit select mode after delete
                  } catch (e) {
                    console.error('Bulk delete error:', e);
                  }
                  break;
                  
                case 'edit-selected':
                case 'bulk-edit':
                  setShowMoveModal(true);
                  break;
                  
                case 'tag':
                  alert('Tag functionality coming soon!');
                  break;
                  
                case 'share':
                  setManageSharingItemIds(Array.from(selectedIds));  // Use current selection
                  setManageSharingItemName(undefined);  // Bulk mode - no specific item name
                  setShowManageSharingModal(true);
                  break;
                  
                case 'assign-qr':
                  setShowQRAssignModal(true);
                  break;
              }
            }}
          kitchenCollectionId={collectionProperties.collection_type === 'grocery' ? collectionProperties.id : null}
          />
  ) : null;

  // Calculate inventory subtitle stats
  const sharedCount = items.filter(item => 
    item.access_type && item.access_type !== 'my_personal'
  ).length;
  const checkedCount = items.filter(item => item.is_checked).length;
  
  // Build subtitle with only non-zero counts
  const subtitleParts = [];
  // When cooking a recipe, show recipe name instead of item count
  if (activeRecipeName) {
    subtitleParts.push(truncateText(activeRecipeName, 30));
  } else {
    subtitleParts.push(`${items.length} items`);
  }
  if (checkedCount > 0) subtitleParts.push(`${checkedCount} checked`);
  // Only show shared count if the collection itself is not shared (avoids redundancy)
  const isCollectionShared = (collectionProperties.shared_with_count && collectionProperties.shared_with_count > 0) || collectionProperties.has_guest_access;
  if (sharedCount > 0 && !isCollectionShared) subtitleParts.push(`${sharedCount} shared`);
  // Show "X displayed" only when not cooking a recipe
  if (!activeRecipeName && filteredItems.length !== items.length) subtitleParts.push(`${filteredItems.length} displayed`);
  if (selectedIds.size > 0) subtitleParts.push(`${selectedIds.size} selected`);
  const inventorySubtitle = subtitleParts.join(' • ');

  return (
    <>
      {isActive && (
        <UnifiedHeader 
          title={truncateText(inventoryState.collection === 'all' ? 'All Items' : inventoryState.collection === 'wandering' ? 'Nomads' : inventoryState.collection === 'shared_with_me' ? 'Shared Items' : collectionName || (lockedCollectionId ? 'Kitchen' : 'Wandering Sock'), 25)}
          subtitle={inventorySubtitle}
          menuBar={inventoryMenuBar}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onSockClick={() => {
            setSearchTerm(''); // Clear search
            // Call voice toggle directly (keeps recognition.start() in user gesture call stack for iOS)
            if (voiceToggleRef.current) {
              voiceToggleRef.current();
            } else if (onSockClick) {
              onSockClick(); // Fallback to parent handler
            }
          }}
          isVoiceListening={isVoiceListening}
          isTumbling={isSockTumbling}
          isTumblingBackward={isSockTumblingBackward}
          showNotifications={true}
          onNavigateToCollection={onNotificationNavigateToCollection}
          onNavigateToGroup={onNotificationNavigateToGroup}
          showCollectionDropdown={!lockedCollectionId}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
          selectedCollection={inventoryState.collection}
          onCollectionChange={setCollection}
          onShowCollectionDetail={handleShowCollectionModal}
          onDeleteCollection={handleDeleteCollection}
          onShareCollection={(id) => setSharingCollectionId(id)}
          onEmptyCollection={handleEmptyCollection}
          collectionRefreshTrigger={collectionRefreshTrigger}
          allItemsCount={allItemsCount}
          wanderingItemsCount={wanderingItemsCount}
          onRegisterToggleSheet={onRegisterToggleSheet}
          hideKitchenCollections={hideKitchenCollections}
        />
      )}
      <PageTemplate
        showPageHeader={false}
        showInputArea={false}
        hasGrayBackground={true}
        enableSmartScroll={false}
        stickyHeader={true}
        stickyInput={true}
        className="inventory-page"
      >
      
      {/* Collection header - show for shared collections (but not if user is owner) */}
      {collectionProperties.permission && Number(collectionProperties.owner_id) !== Number(user?.id) && (
        <SharedCollectionHeader
          collectionName={inventoryState.collection === 'all' ? 'All Items' : collectionName}
          collectionDescription={inventoryState.collection === 'all' ? '' : collectionDescription}
          itemCount={displayItems.length}
          permission={collectionProperties.permission}
          isOwner={false}
          ownerName={collectionProperties.owner_name}
          ownerEmail={collectionProperties.owner_email}
        />
      )}
      
      {/* Bindle description OR collection description - shown above items */}
      {(() => {
        // If a bindle is active and has a description, show it instead of collection description
        if (activeBindle?.description) {
          return (
            <div style={{
              padding: '8px 16px 4px',
              background: 'var(--color-bg-secondary)',
              borderBottom: '1px solid var(--color-border)',
            }}>
              <div style={{
                color: 'var(--color-text-primary)',
                fontSize: 'var(--font-size-md)',
                fontWeight: '700',
                textAlign: 'left',
                textTransform: 'capitalize' as const,
                marginBottom: '4px',
              }}>
                {activeBindle.name}
              </div>
              <div style={{
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-sm)',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                overflow: 'hidden',
                ...(bindleDescExpanded ? {} : {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as const,
                }),
              }}>
                {activeBindle.description}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={() => setBindleDescExpanded(prev => !prev)}
                  style={{
                    padding: '4px 24px',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--color-text-primary)',
                    cursor: 'pointer',
                    lineHeight: '1',
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: bindleDescExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
            </div>
          );
        }

        // Get description for pseudo-collections or regular collections
        const description = inventoryState.collection === 'all' 
          ? PSEUDO_COLLECTIONS.all.description
          : inventoryState.collection === 'wandering'
          ? PSEUDO_COLLECTIONS.wandering.description
          : inventoryState.collection === 'shared_with_me'
          ? PSEUDO_COLLECTIONS.shared_with_me.description
          : collectionDescription;
        
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        return description && !lockedCollectionId ? (
          <div style={{
            padding: '6px 16px 12px',
            color: isDark ? 'rgba(235, 235, 245, 0.6)' : '#6b7280',
            fontSize: 'var(--font-size-md)',
            lineHeight: '1.5',
            fontWeight: '500',
            textAlign: 'center',
            background: 'var(--color-bg-secondary)',
            whiteSpace: 'pre-wrap',
          }}>
            {description}
          </div>
        ) : null;
      })()}

      {/* Stock Up button — in Shop/Manage mode when there are untracked (-1) items */}
      {viewMode.startsWith('stock-checklist-') && (stockChecklistMode === 'shop' || stockChecklistMode === 'manage') && (() => {
        if (!activeBindle || !bindleItemIds) return null;
        const untrackedItems = items.filter(i => (i.quantity ?? -1) === -1 && bindleItemIds.has(i.id));
        if (untrackedItems.length === 0) return null;
        return (
          <div style={{
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <button
              onClick={async () => {
                if (!token) return;
                const updates = untrackedItems.map(i => ({ id: i.id, name: i.name, quantity: 0 }));
                // Optimistic UI update
                setItems(prev => prev.map(i => {
                  const match = untrackedItems.find(u => u.id === i.id);
                  return match ? { ...i, quantity: 0 } : i;
                }));
                try {
                  const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ items: updates }),
                  });
                  if (!resp.ok) {
                    console.error('Failed to stock up items');
                  }
                } catch (err) {
                  console.error('Error stocking up items:', err);
                }
              }}
              style={{
                padding: '4px 12px',
                border: '1px solid var(--color-accent)',
                borderRadius: '14px',
                background: 'transparent',
                color: 'var(--color-accent)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Add {untrackedItems.length} untracked item{untrackedItems.length !== 1 ? 's' : ''} to shopping list
            </button>
          </div>
        );
      })()}

      {/* Re-add missing bindle items — when a bindle is active and some search terms have no matching item */}
      {activeBindle && missingBindleTerms.length > 0 && collectionProperties.permission !== 'view' && (() => {
        return (
          <div style={{
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <button
              onClick={async () => {
                if (!token) return;
                try {
                  const requestBody: any = {
                    items: missingBindleTerms.map(term => ({
                      name: term.replace(/^"|"$/g, ''),
                      quantity: -1,
                    })),
                    duplicate_action: 'use_existing',
                  };
                  if (collectionProperties.id) {
                    requestBody.collection_id = collectionProperties.id;
                  }
                  const resp = await fetch(API_ENDPOINTS.ADD_RECEIPT_TO_COLLECTION, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                  });
                  if (resp.ok) {
                    await fetchItems();
                  }
                } catch (err) {
                  console.error('Error re-adding missing bindle items:', err);
                }
              }}
              style={{
                padding: '4px 12px',
                border: '1px solid var(--color-accent)',
                borderRadius: '14px',
                background: 'transparent',
                color: 'var(--color-accent)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Re-add {missingBindleTerms.length} missing item{missingBindleTerms.length !== 1 ? 's' : ''}
            </button>
          </div>
        );
      })()}
      
      {/* Active Category Filter Chips */}
          {selectedCategoryIds.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '6px',
              padding: '8px 16px',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg-primary)'
            }}>
              {selectedCategoryIds.map(categoryId => {
                const category = allCategories.find(c => c.id === categoryId);
                if (!category) return null;
                return (
                  <button
                    key={categoryId}
                    onClick={() => {
                      setSelectedCategoryIds(prev => prev.filter(id => id !== categoryId));
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      background: 'var(--color-bg-tertiary)',
                      border: '1px solid var(--color-border-strong)',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: 'var(--color-text-secondary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'var(--color-border)';
                      e.currentTarget.style.borderColor = 'var(--color-text-faint)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                      e.currentTarget.style.borderColor = 'var(--color-border-strong)';
                    }}
                  >
                    <span>{category.label}</span>
                    <span style={{ fontSize: '16px', lineHeight: '1' }}>×</span>
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Stock Edit Mode - Special case with Save/Cancel */}
          {stockEditMode && (
            <div className="stock-edit-actions" style={{ 
              display: 'flex', 
              gap: '8px', 
              padding: '8px 12px',
              background: 'rgba(255, 204, 0, 0.1)',
              borderRadius: '12px',
              marginBottom: '12px'
            }}>
              <button
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'var(--color-accent)',
                  color: 'var(--color-text-inverse)',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  if (!token) return;
                  try {
                    setSaving(true);
                    const updates = Object.entries(pendingQuantityChanges).map(([idStr, qty]) => {
                      const id = parseInt(idStr);
                      const item = items.find(i => i.id === id);
                      if (!item) return null;
                      
                      const updatePayload: any = {
                        id,
                        name: item.name,
                        description: item.description,
                        category_ids: item.categories?.map(c => c.id) || [],
                        color: item.color,
                        area: item.area,
                        location: item.location,
                        quantity: qty,
                        quantity_min: item.quantity_min,
                      };
                      
                      // Add purchased_at if it was set for this item
                      if (window.pendingPurchaseDates && window.pendingPurchaseDates[id]) {
                        updatePayload.purchased_at = window.pendingPurchaseDates[id];
                      }
                      
                      return updatePayload;
                    }).filter(Boolean);
                    
                    if (updates.length > 0) {
                      const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ items: updates }),
                      });
                      
                      if (!resp.ok) {
                        const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
                        console.error('Stock save failed:', resp.status, errorData);
                        alert(`Failed to save: ${errorData.error || 'Unknown error'}`);
                        throw new Error(`Save failed: ${resp.status}`);
                      }
                      
                      await fetchItems();
                    }
                    
                    setPendingQuantityChanges({});
                    if (window.pendingPurchaseDates) {
                      window.pendingPurchaseDates = {};
                    }
                    setStockEditMode(false);
                  } catch (e) {
                    console.error('Stock save error:', e);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Save Changes
              </button>
              <button
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'rgba(0, 0, 0, 0.05)',
                  color: 'var(--color-text-primary)',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setPendingQuantityChanges({});
                  setStockEditMode(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}
          
          {/* Display Modal */}
          <DisplayModal
            isOpen={showDisplayModal}
            onClose={() => setShowDisplayModal(false)}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            sortBy={sortBy}
            onSortByChange={handleSortByChange}
            sortDirection={sortDirection}
            onSortDirectionChange={handleSortDirectionChange}
            collection={inventoryState.collection}
            thumbnailSize={thumbnailSize}
            onThumbnailSizeChange={handleThumbnailSizeChange}
            onResetThisCollection={handleResetThisCollection}
            onResetAllCollections={handleResetAllCollections}
            collectionType={collectionProperties?.collection_type === 'shopping' ? 'shopping_list' : 'regular'}
          />
          
          {/* Search Modal */}
          <SearchModal
            isOpen={showSearchModal}
            onClose={() => setShowSearchModal(false)}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            categories={categories}
            selectedCategoryIds={selectedCategoryIds}
            onCategoryIdsChange={setSelectedCategoryIds}
            allCategories={allCategories.map(cat => ({
              id: cat.id,
              key: cat.key,
              label: cat.label,
              parent_key: cat.parent_key || undefined
            }))}
            items={items.map(item => ({
              area: item.area || '',
              location: item.location || ''
            }))}
            selectedAreas={selectedAreas}
            onAreasChange={setSelectedAreas}
            selectedLocations={selectedLocations}
            onLocationsChange={setSelectedLocations}
            stockFilter={stockFilter}
            onStockFilterChange={setStockFilter}
            isStockMode={viewMode === 'stock-list' || viewMode === 'stock-card'}
            selectedDate={dateFilter}
            onDateChange={setDateFilter}
            dateFieldType={dateFieldType}
            onDateFieldTypeChange={setDateFieldType}
            onClear={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedCategoryIds([]);
              setSelectedAreas([]);
              setSelectedLocations([]);
              setStockFilter('off');
              setDateFilter('');
              setDateFieldType('created');
            }}
          />
      
      {/* Shopping List Controls: Floating pill menu - iOS 26 Photos style */}
      {viewMode.startsWith('stock-checklist-') && !isSelectMode && (() => {
        const hasActiveFilters = 
          searchTerm.trim() !== '' ||
          selectedCategoryIds.length > 0 ||
          imageFilterId !== null ||
          selectedAreas.length > 0 ||
          selectedLocations.length > 0 ||
          stockFilter !== 'off' ||
          dateFilter !== '';
        
        return (
        <FloatingActionBar>
          <>
          {/* Clear Filter button - shows when filters are active */}
          {hasActiveFilters && (
            <>
              <button
                onClick={() => {
                  if (isVoiceListening && voiceToggleRef.current) voiceToggleRef.current();
                  setSearchTerm('');
                  setSelectedCategoryIds([]);
                  setImageFilterId(null);
                  setSelectedAreas([]);
                  setSelectedLocations([]);
                  setStockFilter('off');
                  setDateFilter('');
                  setDateFieldType('created');
                  setActiveRecipeName(null);
                  localStorage.removeItem('inventory-searchTerm');
                  localStorage.removeItem('inventory-stockFilter');
                }}
                style={{
                  padding: '5px 10px',
                  background: 'var(--color-warning)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Clear Filter
              </button>
              <div style={{ width: '1px', height: '16px', background: 'var(--color-border-strong)', margin: '0 2px' }} />
            </>
          )}
          {/* Mode toggle buttons - blue text for visibility */}
          <button
            onClick={() => setStockChecklistMode('manage')}
            style={{
              padding: '5px 10px',
              background: stockChecklistMode === 'manage' ? 'var(--color-accent-light)' : 'transparent',
              color: stockChecklistMode === 'manage' ? 'var(--color-accent-secondary)' : 'var(--color-text-secondary)',
              border: 'none',
              borderRadius: '14px',
              fontSize: '13px',
              fontWeight: stockChecklistMode === 'manage' ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Manage
          </button>
          <button
            onClick={() => setStockChecklistMode('shop')}
            style={{
              padding: '5px 10px',
              background: stockChecklistMode === 'shop' ? 'var(--color-accent-light)' : 'transparent',
              color: stockChecklistMode === 'shop' ? 'var(--color-accent-secondary)' : 'var(--color-text-secondary)',
              border: 'none',
              borderRadius: '14px',
              fontSize: '13px',
              fontWeight: stockChecklistMode === 'shop' ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Shop
          </button>
          
          {/* Divider - subtle iOS style */}
          {stockChecklistMode === 'shop' && (
            <div style={{ width: '1px', height: '16px', background: 'var(--color-border-strong)', margin: '0 2px' }} />
          )}
          
          {/* Qty: 0 toggle - only in Shop mode */}
          {stockChecklistMode === 'shop' && (
            <button
              onClick={() => setShowAllStockChecklistItems(!showAllStockChecklistItems)}
              style={{
                padding: '5px 10px',
                background: showAllStockChecklistItems ? 'var(--color-warning)' : 'transparent',
                color: showAllStockChecklistItems ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                border: 'none',
                borderRadius: '14px',
                fontSize: '13px',
                fontWeight: showAllStockChecklistItems ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              0 only
            </button>
          )}
          
          {/* New Item / Add Missing button - show in both Shop and Manage modes */}
          {viewMode.startsWith('stock-checklist-') && (
            <>
              <div style={{ width: '1px', height: '16px', background: 'var(--color-border-strong)', margin: '0 2px' }} />
              {unmatchedTerms.length > 0 ? (
              <button
                onClick={() => setShowAddMissingModal(true)}
                style={{
                  padding: '5px 10px',
                  background: 'var(--color-accent)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add
              </button>
              ) : (
              <button
                onClick={() => {
                  setAddItemModalDefaultTab('manual');
                  setShowAddItemModal(true);
                }}
                style={{
                  padding: '5px 10px',
                  background: 'transparent',
                  color: 'var(--color-accent-secondary)',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add
              </button>
              )}
            </>
          )}
          
          {/* Undo button - shows temporarily after auto-save */}
          {undoVisible && undoStack.length > 0 && (
            <>
              <div style={{ width: '1px', height: '16px', background: 'rgba(0, 0, 0, 0.12)', margin: '0 2px' }} />
              <button
                onClick={async () => {
                  if (!token || !inventoryState.collection || inventoryState.collection === 'all') return;
                  
                  // Trigger header sock backward tumble animation
                  setIsSockTumblingBackward(true);
                  setTimeout(() => setIsSockTumblingBackward(false), 600);
                  
                  if (undoTimerRef.current) {
                    clearTimeout(undoTimerRef.current);
                  }
                  
                  try {
                    for (const action of undoStack) {
                      if (action.type === 'quantity') {
                        const item = items.find(i => i.id === action.itemId);
                        if (!item) continue;
                        
                        const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            items: [{ id: action.itemId, name: item.name, quantity: action.previousValue }]
                          }),
                        });
                        
                        if (resp.ok) {
                          setItems(prevItems => 
                            prevItems.map(i => i.id === action.itemId ? { ...i, quantity: action.previousValue as number } : i)
                          );
                        }
                      } else if (action.type === 'check') {
                        const undoCollectionId = getNumericCollectionId(inventoryState.collection);
                        if (!undoCollectionId) continue;
                        
                        const resp = await fetch(`${API_BASE_URL}/api/collections/${undoCollectionId}/items/${action.itemId}/check`, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({ is_checked: action.previousValue })
                        });
                        
                        if (resp.ok) {
                          setItems(prevItems => 
                            prevItems.map(i => i.id === action.itemId ? { ...i, is_checked: action.previousValue as boolean } : i)
                          );
                        }
                      }
                    }
                  } catch (error) {
                    console.error('Failed to undo:', error);
                  }
                  
                  setUndoStack([]);
                  setUndoVisible(false);
                }}
                style={{
                  padding: '5px 10px',
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: 'var(--color-accent-secondary)',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                }}
                title="Undo recent changes"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 4v6h6"></path>
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                </svg>
                Undo
              </button>
            </>
          )}
          </>
        </FloatingActionBar>
        );
      })()}
      
      {/* Context-sensitive help tip for shopping list mode */}
      {viewMode.startsWith('stock-checklist-') && (
        <ContextHelpTip
          tipId={stockChecklistMode === 'manage' ? 'stock-checklist-manage' : 'stock-checklist-shop'}
          bottomOffset={130}
          show={true}
        />
      )}
      
      {/* Context-sensitive help tip for general inventory view */}
      {!viewMode.startsWith('stock-checklist-') && inventoryState.collection === 'all' && (
        <ContextHelpTip
          tipId="inventory-first-visit"
          bottomOffset={130}
          show={true}
        />
      )}
      
      
      {/* Floating action bar for generic/stock views - shows Clear Filter and New Item buttons */}
      {!viewMode.startsWith('stock-checklist-') && 
       inventoryState.collection !== 'shared_with_me' &&
       !inventoryState.collection.startsWith('shared_with_group_') &&
       !isSelectMode && (() => {
        const hasActiveFilters = 
          searchTerm.trim() !== '' ||
          selectedCategoryIds.length > 0 ||
          imageFilterId !== null ||
          selectedAreas.length > 0 ||
          selectedLocations.length > 0 ||
          stockFilter !== 'off' ||
          dateFilter !== '';
        
        return (
        <FloatingActionBar>
          <>
          {/* Clear Filter button - shows when filters are active */}
          {hasActiveFilters && (
            <>
              <button
                onClick={() => {
                  if (isVoiceListening && voiceToggleRef.current) voiceToggleRef.current();
                  setSearchTerm('');
                  setSelectedCategoryIds([]);
                  setImageFilterId(null);
                  setSelectedAreas([]);
                  setSelectedLocations([]);
                  setStockFilter('off');
                  setDateFilter('');
                  setDateFieldType('created');
                  setActiveRecipeName(null);
                  localStorage.removeItem('inventory-searchTerm');
                  localStorage.removeItem('inventory-stockFilter');
                }}
                style={{
                  padding: '5px 10px',
                  background: 'var(--color-warning)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Clear Filter
              </button>
              <div style={{ width: '1px', height: '16px', background: 'var(--color-border-strong)', margin: '0 2px' }} />
            </>
          )}
          {/* New Item / Add Missing button - hide if user has view-only permission */}
          {collectionProperties.permission !== 'view' && viewMode.startsWith('stock-checklist-') ? (
            unmatchedTerms.length > 0 ? (
            <button
              onClick={() => setShowAddMissingModal(true)}
              style={{
                padding: '5px 10px',
                background: 'var(--color-accent)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '14px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add
            </button>
            ) : (
            <button
              onClick={() => {
                    setAddItemModalDefaultTab('scan');
                    setShowAddItemModal(true);
                  }}
              style={{
                padding: '5px 10px',
                background: 'transparent',
                color: 'var(--color-accent-secondary)',
                border: 'none',
                borderRadius: '14px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add
          </button>
            )
          ) : (
          <button
            onClick={() => {
              setAddItemModalDefaultTab('manual');
              setShowAddItemModal(true);
            }}
            style={{
              padding: '5px 10px',
              background: 'transparent',
              color: 'var(--color-accent-secondary)',
              border: 'none',
              borderRadius: '14px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add
          </button>
          )}
          </>
        </FloatingActionBar>
        );
      })()}
      
      {/* Floating Exit Select Mode button - shows when in select mode */}
      {isSelectMode && (
        <div style={{
          position: 'fixed',
          bottom: Platform.isNative ? 'calc(env(safe-area-inset-bottom, 0px) + 70px)' : '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
        }}>
          <button
            onClick={() => {
              exitSelectMode();
              setMode('view');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 20px',
              background: 'rgba(37, 99, 235, 0.95)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              borderRadius: '22px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
              border: 'none',
              color: 'var(--color-text-inverse)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Exit Select Mode
          </button>
        </div>
      )}
      
      {/* Simple active filter chip for image filter */}
      {imageFilterId != null && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe',
          padding: '6px 10px', borderRadius: 9999, margin: '8px 12px', fontSize: 13
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="9.5" r="1.5"></circle>
            <path d="M21 16l-5-5-4 4-2-2-4 4"></path>
          </svg>
          <span style={{ opacity: 0.75 }}>{formatImageGroupDate(imageFilterId) ?? `#${imageFilterId}`}</span>
          <button
            onClick={() => setImageFilterId(null)}
            style={{ background: 'transparent', border: 'none', color: '#3730a3', cursor: 'pointer', fontWeight: 700 }}
            aria-label="Clear image filter"
          >×</button>
        </div>
      )}
      {!isSharingModalOpen && (
        <>
        {/* Subtle loading indicator at top */}
        {loading && (
          <div style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #007AFF 0%, #007AFF 50%, transparent 50%, transparent 100%)',
            backgroundSize: '200% 100%',
            animation: 'loadingSlide 1.5s ease-in-out infinite',
            zIndex: 10,
          }} />
        )}
        
        {/* Empty state for shopping list Shop mode when no items to shop */}
        {viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop' && displayItems.length === 0 && items.length > 0 && (
          <EmptyState
            messageId="shopping-list-shop-empty"
            action={{
              label: 'Switch to Manage',
              onClick: () => setStockChecklistMode('manage'),
            }}
          />
        )}
        
        {/* Regular inventory content */}
        {(displayItems.length > 0 || !(viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop' && items.length > 0)) && (
        <InventoryContent
          items={
            displayItems.length === 0 && items.length === 0 && 
            lastItemsRef.current.items.length > 0 && 
            lastItemsRef.current.collection === inventoryState.collection
              ? lastItemsRef.current.items
              : displayItems
          }
          loading={loading}
          token={token}
          user={user}
          currentUserId={user?.id}
          isSharedCollection={
            (!!collectionProperties.shared_with_count && collectionProperties.shared_with_count > 0) ||
            !!collectionProperties.has_guest_access
          }
          selectedIds={selectedIds}
          thumbnailSize={thumbnailSize}
          bindleItemIds={bindleItemIds}
          hasActiveFilter={searchTerm.trim() !== '' || selectedCategory !== 'all' || selectedCategoryIds.length > 0 || imageFilterId !== null || selectedAreas.length > 0 || selectedLocations.length > 0 || stockFilter !== 'off' || dateFilter !== ''}
          totalItemsBeforeFilter={items.length}
          viewMode={
            // In shopping list Manage mode, show stock view
            viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'manage'
              ? (viewMode === 'stock-checklist-card' ? 'stock-card' : 'stock-list')
              : viewMode
          }
          isEditMode={activeTab === 'bulk' || viewMode === 'stock-list' || viewMode === 'stock-card' || (viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'manage')}
          isSelectMode={isSelectMode}
          onItemClick={(item) => {
            if (isSelectMode) {
              toggleSelection(item.id);
            }
          }}
          onMenuClick={handleItemLongPress as any}
          onQuantityChange={(viewMode === 'stock-list' || viewMode === 'stock-card' || (viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'manage')) ? async (itemId, newQuantity) => {
            const item = items.find(i => i.id === itemId);
            if (!item || !token) return;
            
            const previousQuantity = item.quantity ?? 0;
            
            // For shopping list Manage mode: auto-save immediately
            if (viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'manage') {
              // Check if this is a shopping list and quantity is increasing
              const isShoppingList = collectionPropertiesRef.current.collection_type === 'shopping' || collectionPropertiesRef.current.collection_type === 'grocery';
              const isQuantityIncreasing = newQuantity > previousQuantity;
              const shouldUpdatePurchaseDate = isShoppingList && isQuantityIncreasing;
              
              // Optimistically update UI
              const now = new Date().toISOString();
              setItems(prevItems => 
                prevItems.map(i => i.id === itemId ? { 
                  ...i, 
                  quantity: newQuantity,
                  ...(shouldUpdatePurchaseDate ? { purchased_at: now } : {})
                } : i)
              );
              
              // Save to backend
              try {
                const updatePayload: any = { 
                  id: itemId, 
                  name: item.name, 
                  quantity: newQuantity 
                };
                
                // Add purchased_at if quantity is increasing in shopping list
                if (shouldUpdatePurchaseDate) {
                  updatePayload.purchased_at = now;
                }
                
                const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    items: [updatePayload]
                  }),
                });
                
                if (resp.ok) {
                  // Show undo button
                  showUndoWithTimer({
                    type: 'quantity',
                    itemId,
                    previousValue: previousQuantity,
                    newValue: newQuantity,
                    timestamp: Date.now(),
                  });
                } else {
                  // Revert on failure
                  setItems(prevItems => 
                    prevItems.map(i => i.id === itemId ? { ...i, quantity: previousQuantity } : i)
                  );
                  console.error('Failed to save quantity');
                }
              } catch (error) {
                // Revert on error
                setItems(prevItems => 
                  prevItems.map(i => i.id === itemId ? { ...i, quantity: previousQuantity } : i)
                );
                console.error('Error saving quantity:', error);
              }
            } else {
              // For regular stock views: use pending changes (existing behavior)
              // But if this is a shopping list and quantity is increasing, also update purchased_at
              const isShoppingList = collectionPropertiesRef.current.collection_type === 'shopping' || collectionPropertiesRef.current.collection_type === 'grocery';
              const isQuantityIncreasing = newQuantity > previousQuantity;
              
              if (isShoppingList && isQuantityIncreasing) {
                const now = new Date().toISOString();
                
                // Update UI optimistically with new quantity and purchased_at
                setItems(prevItems => 
                  prevItems.map(i => i.id === itemId ? { 
                    ...i, 
                    quantity: newQuantity,
                    purchased_at: now
                  } : i)
                );
                // Also add to pending changes with purchased_at
                setPendingQuantityChanges(prev => ({ 
                  ...prev, 
                  [itemId]: newQuantity 
                }));
                // Store purchased_at separately for later save
                if (!window.pendingPurchaseDates) {
                  window.pendingPurchaseDates = {};
                }
                window.pendingPurchaseDates[itemId] = now;
              } else {
                setPendingQuantityChanges(prev => ({ ...prev, [itemId]: newQuantity }));
              }
            }
          } : undefined}
          onPhotoUpload={async (itemId, file) => {
            // Handle photo upload directly from card view
            if (!token) {
              console.error('No token available');
              return;
            }
            
            const item = items.find(i => i.id === itemId);
            if (!item) {
              console.error('Item not found:', itemId);
              return;
            }
            
            try {
              setSaving(true);
              
              // Upload new image
              const formData = new FormData();
              formData.append('image', file);
              formData.append('create_image', 'true');
              formData.append('upload_only', 'true'); // Skip analysis, just upload
              
              const uploadResp = await fetch(API_ENDPOINTS.ANALYZE, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
              });
              
              let newImageId: number | undefined;
              if (uploadResp.status === 409) {
                // Image already exists with items - extract the image_id from the response
                const conflictData = await uploadResp.json();
                newImageId = conflictData.image_id;
              } else if (!uploadResp.ok) {
                console.error('Upload failed with status:', uploadResp.status);
                throw new Error('Image upload failed');
              } else {
                const data = await uploadResp.json();
                newImageId = data.image_id || data.image?.id;
              }
              
              if (!newImageId) {
                console.error('No image_id in response');
                throw new Error('No image_id received from upload');
              }
              
              // Update item with new image_id
              const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  items: [{
                    id: item.id,
                    name: item.name,
                    description: item.description,
                    category_ids: item.categories?.map(c => c.id) || [],
                    color: item.color,
                    area: item.area,
                    location: item.location,
                    sub_location: item.sub_location,
                    quantity: item.quantity,
                    quantity_min: item.quantity_min,
                    image_id: newImageId,
                  }],
                }),
              });
              
              if (!resp.ok) {
                console.error('Item update failed with status:', resp.status);
                throw new Error('Update failed');
              }
              
              // Refresh items to show new image
              await fetchItems();
              
              // Small delay to ensure state updates propagate
              await new Promise(resolve => setTimeout(resolve, 100));
            } catch (e) {
              console.error('Photo upload failed', e);
              alert('Failed to upload photo. Please try again.');
            } finally {
              setSaving(false);
            }
          }}
          onOpenImageSelection={(itemId: number) => {
            // Find the item and set it as editItem, then open the modal
            const item = items.find(i => i.id === itemId);
            if (!item) {
              console.error('Item not found:', itemId);
              return;
            }
            
            // Convert to EditableItem format
            const editable: EditableItem = {
              id: item.id,
              name: item.name,
              description: item.description || '',
              categories: (item.categories || []).filter(cat => cat.key && cat.label).map(cat => ({
                id: cat.id,
                key: cat.key!,
                label: cat.label!
              })),
              color: item.color || '',
              area: item.area || '',
              location: item.location || '',
              sub_location: item.sub_location || '',
              quantity: item.quantity ?? 0,
              quantity_min: item.quantity_min || undefined,
              image_id: item.image_id || null,
              created_at: item.created_at,
              updated_at: item.updated_at,
            };
            
            // Restore images from cache for this item, or trigger fresh fetch
            const cached = getCachedImages(editable.name);
            const cachedPhotos = getCachedPhotoData(editable.name);
            if (cached && cached.length > 0 && cachedPhotos && cachedPhotos.length > 0) {
              setEditModalSearchImages(cached);
              setEditModalPhotoData(cachedPhotos);
            } else {
              setEditModalSearchImages([]);
              setEditModalPhotoData([]);
            }
            setImageSearchPage(1);
            setEditItem(editable);
            setShowImageSelectionModal(true);
          }}
          onCheckboxToggle={async (item: any, e: React.MouseEvent) => {
            e.stopPropagation();
            if (!token || !inventoryState.collection || inventoryState.collection === 'all') return;
            
            // Extract numeric collection ID from various formats
            let collectionId: string | null = null;
            if (inventoryState.collection.startsWith('shared-')) {
              collectionId = inventoryState.collection.replace('shared-', '');
            } else if (inventoryState.collection.startsWith('merged-')) {
              // For merged collections, use the owned collection ID (first one)
              const ids = inventoryState.collection.replace('merged-', '').split('-');
              collectionId = ids[0];
            } else if (!isNaN(Number(inventoryState.collection))) {
              collectionId = inventoryState.collection;
            }
            
            if (!collectionId) return;
            
            // Trigger header sock tumble animation
            setIsSockTumbling(true);
            setTimeout(() => setIsSockTumbling(false), 600); // Match animation duration
            
            const originalItems = [...items];
            const previousQuantity = item.quantity ?? 0;
            
            // Shopping list Shop mode: checking = increment qty + clear check
            if (viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop') {
              if (!item.is_checked) {
                // Checking: increment quantity by 1, item stays unchecked (or briefly checked then cleared)
                const newQuantity = previousQuantity + 1;
                const now = new Date().toISOString();
                
                // Start fade-out animation if "0 only" filter is on (item will disappear)
                // showAllStockChecklistItems = false means we're filtering to show only zero items
                if (!showAllStockChecklistItems) {
                  setFadingOutItems(prev => new Set(prev).add(item.id));
                  // Trigger sock tumble animation
                  setIsSockTumbling(true);
                  setTimeout(() => setIsSockTumbling(false), 600);
                }
                
                // Wait for fade animation before updating quantity (2 second animation)
                await new Promise(resolve => setTimeout(resolve, !showAllStockChecklistItems ? 2000 : 0));
                
                // Update quantity and purchased_at (this will filter out the item if "0 only" is on)
                setItems(prevItems => 
                  prevItems.map(i => 
                    i.id === item.id ? { ...i, quantity: newQuantity, purchased_at: now } : i
                  )
                );
                
                // Remove from fading set
                setFadingOutItems(prev => {
                  const next = new Set(prev);
                  next.delete(item.id);
                  return next;
                });
                
                try {
                  // Increment quantity on backend with purchased_at
                  const updatePayload: any = { 
                    id: item.id, 
                    name: item.name, 
                    quantity: newQuantity,
                    purchased_at: now
                  };
                  
                  const updateResp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      items: [updatePayload]
                    })
                  });
                  
                  if (!updateResp.ok) {
                    console.error('Failed to update quantity');
                    setItems(originalItems);
                    return;
                  }
                  
                  // Show undo button
                  showUndoWithTimer({
                    type: 'quantity',
                    itemId: item.id,
                    previousValue: previousQuantity,
                    newValue: newQuantity,
                    timestamp: Date.now(),
                  });
                } catch (error) {
                  console.error('Error updating quantity:', error);
                  setItems(originalItems);
                }
              }
              // If already checked (unchecking), do nothing in Shop mode
              return;
            }
            
            // Regular checklist mode: just toggle check state
            const newCheckedState = !item.is_checked;
            const previousCheckedState = item.is_checked;
            
            // Optimistically update UI
            setItems(prevItems => 
              prevItems.map(i => 
                i.id === item.id ? { ...i, is_checked: newCheckedState } : i
              )
            );
            
            try {
              const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/items/${item.id}/check`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_checked: newCheckedState })
              });
              
              if (!response.ok) {
                console.error('Failed to toggle item:', response.status);
                setItems(originalItems);
              }
            } catch (error) {
              console.error('Error toggling item:', error);
              setItems(originalItems);
            }
          }}
          showQuantityInName={viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop'}
          highlightZeroQuantity={viewMode.startsWith('stock-checklist-') || viewMode.startsWith('marketplace-')}
          fadingOutItems={fadingOutItems}
          // Swipe actions based on context
          swipeEnabled={!isSelectMode}
          leftSwipeAction={
            // Stock/Manage/Shop/Marketplace modes: decrease quantity
            (viewMode.startsWith('stock-') || viewMode.startsWith('marketplace-') || (viewMode.startsWith('stock-checklist-') && (stockChecklistMode === 'manage' || stockChecklistMode === 'shop')))
              ? 'decrease-quantity'
              : inventoryState.collection === 'all' 
                ? 'delete' 
                : 'remove-from-collection'
          }
          rightSwipeAction={
            // Stock/Manage/Shop/Marketplace modes: increase quantity
            (viewMode.startsWith('stock-') || viewMode.startsWith('marketplace-') || (viewMode.startsWith('stock-checklist-') && (stockChecklistMode === 'manage' || stockChecklistMode === 'shop')))
              ? 'increase-quantity'
              : inventoryState.collection === 'all'
                ? 'add-to-collection'
                : undefined
          }
          onSwipeAction={handleSwipeAction}
          useQuantityBadge={
            viewMode.startsWith('stock-') || 
            viewMode.startsWith('marketplace-') ||
            (viewMode.startsWith('stock-checklist-') && (stockChecklistMode === 'manage' || stockChecklistMode === 'shop'))
          }
          onQuantityBadgeClick={(item) => setQuantityEditItem(item)}
          disableNameEdit={viewMode.startsWith('stock-checklist-') && stockChecklistMode === 'shop'}
        />
        )}
        </>
      )}

      {/* Quantity Edit Modal */}
      {quantityEditItem && (
        <QuantityEditModal
          isOpen={true}
          itemName={quantityEditItem.name}
          quantity={quantityEditItem.quantity ?? 0}
          onClose={() => setQuantityEditItem(null)}
          onSave={async (newQuantity) => {
            if (!token) return;
            const previousQuantity = quantityEditItem.quantity ?? 0;
            
            // Optimistically update UI
            setItems(prev => prev.map(i => 
              i.id === quantityEditItem.id ? { ...i, quantity: newQuantity } : i
            ));
            
            try {
              const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  items: [{ id: quantityEditItem.id, name: quantityEditItem.name, quantity: newQuantity }]
                }),
              });
              
              if (resp.ok) {
                showUndoWithTimer({
                  type: 'quantity',
                  itemId: quantityEditItem.id,
                  previousValue: previousQuantity,
                  newValue: newQuantity,
                  timestamp: Date.now(),
                });
              } else {
                // Revert on failure
                setItems(prev => prev.map(i => 
                  i.id === quantityEditItem.id ? { ...i, quantity: previousQuantity } : i
                ));
              }
            } catch (error) {
              console.error('Failed to update quantity:', error);
              setItems(prev => prev.map(i => 
                i.id === quantityEditItem.id ? { ...i, quantity: previousQuantity } : i
              ));
            }
          }}
        />
      )}

      {/* Swipe Confirmation Dialog (Delete / Remove from Collection) */}
      {swipeConfirmItem && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setSwipeConfirmItem(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-bg-primary)',
              borderRadius: '16px',
              padding: '20px',
              minWidth: '280px',
              maxWidth: '320px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: '12px',
              textAlign: 'center',
            }}>
              {swipeConfirmItem.action === 'delete' ? 'Delete Item?' : 'Remove from Collection?'}
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--color-text-muted)',
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              {swipeConfirmItem.action === 'delete' 
                ? `"${swipeConfirmItem.item.name}" will be moved to trash.`
                : `"${swipeConfirmItem.item.name}" will be removed from this collection.`
              }
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setSwipeConfirmItem(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-strong)',
                  background: 'var(--color-bg-primary)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '15px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const { item, action } = swipeConfirmItem;
                  setSwipeConfirmItem(null);
                  
                  if (action === 'delete') {
                    try {
                      const resp = await fetch(API_ENDPOINTS.DELETE_ITEM(item.id), {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` },
                      });
                      if (resp.ok) {
                        setItems(prev => prev.filter(i => i.id !== item.id));
                      }
                    } catch (error) {
                      console.error('Failed to delete item:', error);
                    }
                  } else if (action === 'remove-from-collection' && collectionProperties.id) {
                    try {
                      const resp = await fetch(
                        API_ENDPOINTS.REMOVE_ITEM_FROM_COLLECTION(collectionProperties.id, item.id),
                        {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${token}` },
                        }
                      );
                      if (resp.ok) {
                        // Refresh items from backend to ensure UI is in sync
                        await fetchItems();
                        // Refresh counts to update Nomads count immediately
                        await refetchCounts();
                        // Refresh collection counts since item was removed
                        setCollectionRefreshTrigger(prev => prev + 1);
                      }
                    } catch (error) {
                      console.error('Failed to remove from collection:', error);
                    }
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  background: swipeConfirmItem.action === 'delete' ? 'var(--color-danger)' : 'var(--color-warning)',
                  color: 'var(--color-text-inverse)',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {swipeConfirmItem.action === 'delete' ? 'Delete' : 'Remove'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add to Collection Modal */}
      {addToCollectionItem && (
        <ManageCollectionsModal
          selectedItemIds={[addToCollectionItem.id]}
          onClose={() => setAddToCollectionItem(null)}
          onSuccess={async () => {
            setAddToCollectionItem(null);
            // Invalidate cache after collection changes
            invalidateCollectionCache(inventoryState.collection);
            invalidateCountsCache();
            await refetchCounts();
          }}
        />
      )}

      {showContextMenu && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => closeContextMenu()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              position: 'relative',
              background: 'var(--color-bg-primary)',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: '0',
              width: '100vw',
              maxWidth: '100vw',
              maxHeight: '85vh',
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Handle bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 0 4px 0'
            }}>
              <div style={{
                width: '36px',
                height: '5px',
                backgroundColor: 'var(--color-border-strong)',
                borderRadius: '3px'
              }} />
            </div>

            {/* Title with Item ID */}
            {contextItem && (
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                padding: '6px 16px 12px 16px',
                textAlign: 'center',
                borderBottom: '1px solid var(--color-border)',
              }}>
                Item #{contextItem.id}
              </div>
            )}

            {/* Show Edit button only in single-item mode and if user owns the item or has edit permission */}
            {!(isSelectMode && selectedIds.size > 0) && contextItem && (
              contextItem.access_type === 'my_personal' || 
              contextItem.my_role !== 'viewer'
            ) && (
            <button
              style={{ 
                border: 'none', 
                background: 'transparent', 
                textAlign: 'left', 
                padding: '16px 20px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                fontSize: '17px',
                color: 'var(--color-text-primary)',
                borderRadius: 0,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                closeContextMenu();
                if (isSelectMode && selectedIds.size > 0) {
                  // Bulk edit selected
                  setShowMoveModal(true);
                } else if (contextItem) {
                  // Single-item edit
                  console.log('[InventoryView] Opening edit for item:', {
                    id: contextItem.id,
                    name: contextItem.name,
                    created_at: contextItem.created_at,
                    updated_at: (contextItem as any).updated_at,
                  });
                  const editable: EditableItem = {
                    ...(contextItem as any),
                  };
                  console.log('[InventoryView] EditableItem created:', {
                    id: editable.id,
                    name: editable.name,
                    created_at: editable.created_at,
                    updated_at: editable.updated_at,
                  });
                  setEditItem(editable);
                  setEditOpen(true);
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              <span>Edit</span>
            </button>
            )}

            {/* Show Change Image button only in single-item mode and if user owns the item or has edit permission */}
            {!(isSelectMode && selectedIds.size > 0) && contextItem && (
              contextItem.access_type === 'my_personal' || 
              contextItem.my_role !== 'viewer'
            ) && (
            <button
              style={{ 
                border: 'none', 
                background: 'transparent', 
                textAlign: 'left', 
                padding: '16px 20px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                fontSize: '17px',
                color: 'var(--color-text-primary)',
                borderRadius: 0,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                closeContextMenu();
                if (contextItem) {
                  const editable: EditableItem = {
                    ...(contextItem as any),
                  };
                  // Restore images from cache for this item, or trigger fresh fetch
                  const cached = getCachedImages(editable.name);
                  const cachedPhotos = getCachedPhotoData(editable.name);
                  if (cached && cached.length > 0 && cachedPhotos && cachedPhotos.length > 0) {
                    setEditModalSearchImages(cached);
                    setEditModalPhotoData(cachedPhotos);
                  } else {
                    setEditModalSearchImages([]);
                    setEditModalPhotoData([]);
                  }
                  setImageSearchPage(1);
                  setEditItem(editable);
                  editItemRef.current = editable; // Sync ref immediately
                  setShowImageSelectionModal(true);
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <span>Change Image</span>
            </button>
            )}

            {/* Collection... submenu */}
            <div style={{ position: 'relative' }}>
              <button
                style={{ 
                  border: 'none', 
                  background: showCollectionSubmenu ? 'var(--color-bg-tertiary)' : 'transparent', 
                  textAlign: 'left', 
                  padding: '16px 20px', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  width: '100%',
                  fontSize: '17px',
                  color: 'var(--color-accent-secondary)',
                  borderRadius: 0,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                onMouseLeave={(e) => !showCollectionSubmenu && (e.currentTarget.style.background = 'transparent')}
                onClick={() => setShowCollectionSubmenu(!showCollectionSubmenu)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>Collection...</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 'auto', transform: showCollectionSubmenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {/* Submenu options */}
              {showCollectionSubmenu && (
                <div style={{ 
                  marginLeft: '24px', 
                  borderLeft: '2px solid var(--color-border)',
                  paddingLeft: '8px',
                }}>
                  {/* Add to collection */}
                  <button
                    style={{ 
                      border: 'none', 
                      background: 'transparent', 
                      textAlign: 'left', 
                      padding: '10px 12px', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      fontSize: '14px',
                      color: 'var(--color-text-primary)',
                      borderRadius: 6,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    onClick={() => {
                      closeContextMenu();
                      setShowCollectionSubmenu(false);
                      setCollectionActionMode('add');
                      if (!isSelectMode || selectedIds.size === 0) {
                        if (contextItem) {
                          clearSelection();
                          toggleSelection(contextItem.id);
                          toggleItemSelection(contextItem.id);
                        }
                      }
                      setShowAddToCollectionModal(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                      <line x1="12" y1="11" x2="12" y2="17"></line>
                      <line x1="9" y1="14" x2="15" y2="14"></line>
                    </svg>
                    <span>Add to...</span>
                  </button>
                  
                  {/* Move to collection - only when viewing a specific collection */}
                  {collectionProperties.id && inventoryState.collection !== 'all' && (
                    <button
                      style={{ 
                        border: 'none', 
                        background: 'transparent', 
                        textAlign: 'left', 
                        padding: '10px 12px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        fontSize: '14px',
                        color: 'var(--color-text-primary)',
                        borderRadius: 6,
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      onClick={() => {
                        closeContextMenu();
                        setShowCollectionSubmenu(false);
                        setCollectionActionMode('move');
                        if (!isSelectMode || selectedIds.size === 0) {
                          if (contextItem) {
                            clearSelection();
                            toggleSelection(contextItem.id);
                            toggleItemSelection(contextItem.id);
                          }
                        }
                        setShowAddToCollectionModal(true);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                        <polyline points="12 8 16 12 12 16"></polyline>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                      <span>Move to...</span>
                    </button>
                  )}
                  
                  {/* Remove from current collection - only when viewing a specific collection */}
                  {collectionProperties.id && inventoryState.collection !== 'all' && (
                    <button
                      style={{ 
                        border: 'none', 
                        background: 'transparent', 
                        textAlign: 'left', 
                        padding: '10px 12px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        fontSize: '14px',
                        color: 'var(--color-danger)',
                        borderRadius: 6,
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-danger-light)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      onClick={async () => {
                        closeContextMenu();
                        setShowCollectionSubmenu(false);
                        const idsToRemove: number[] = [];
                        if (isSelectMode && selectedIds.size > 0) {
                          idsToRemove.push(...Array.from(selectedIds));
                        } else if (contextItem) {
                          idsToRemove.push(contextItem.id);
                        }
                        if (idsToRemove.length === 0) return;

                        try {
                          const collectionId = collectionProperties.id!;
                          for (const itemId of idsToRemove) {
                            const resp = await fetch(
                              API_ENDPOINTS.REMOVE_ITEM_FROM_COLLECTION(collectionId, itemId),
                              {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` },
                              }
                            );
                            if (!resp.ok) {
                              console.error('Remove from collection failed for', itemId);
                            }
                          }
                          await fetchItems();
                          // Refresh collection counts since items were removed
                          setCollectionRefreshTrigger(prev => prev + 1);
                          clearSelectionState();
                          setMode('view');
                        } catch (e) {
                          console.error('Remove from collection failed', e);
                          alert('Failed to remove item(s) from collection');
                          await fetchItems();
                        }
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                        <line x1="9" y1="14" x2="15" y2="14"></line>
                      </svg>
                      <span>Remove from here</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Manage Sharing - only if user owns the item or has edit permission */}
            {contextItem && (
              contextItem.access_type === 'my_personal' || 
              contextItem.my_role !== 'viewer'
            ) && (
            <button
              style={{ 
                border: 'none', 
                background: 'transparent', 
                textAlign: 'left', 
                padding: '12px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                fontSize: '15px',
                color: 'var(--color-accent-secondary)',
                borderRadius: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                closeContextMenu();
                // Set the item IDs for the modal directly (avoid race conditions with selectedIds)
                if (isSelectMode && selectedIds.size > 0) {
                  setManageSharingItemIds(Array.from(selectedIds));
                } else if (contextItem) {
                  setManageSharingItemIds([contextItem.id]);
                }
                setManageSharingItemName(contextItem?.name);
                setShowManageSharingModal(true);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              <span>Manage Sharing</span>
            </button>
            )}

            {/* Auction — single item only, owner only */}
            {!(isSelectMode && selectedIds.size > 0) && contextItem && (
              contextItem.access_type === 'my_personal'
            ) && (
            <button
              style={{ 
                border: 'none', 
                background: 'transparent', 
                textAlign: 'left', 
                padding: '12px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                fontSize: '15px',
                color: '#059669',
                borderRadius: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                closeContextMenu();
                if (contextItem) {
                  setAuctionSetupItemId(contextItem.id);
                  setAuctionSetupItemName(contextItem.name || `Item #${contextItem.id}`);
                  setAuctionSetupSalePrice((contextItem as any).sale_price ?? null);
                  setShowAuctionSetupModal(true);
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
              <span>Auction</span>
            </button>
            )}

            {/* Show Delete button only if user owns the item or has edit permission */}
            {contextItem && (
              contextItem.access_type === 'my_personal' || 
              contextItem.my_role !== 'viewer'
            ) && (
            <button
              style={{
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                padding: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                fontSize: '15px',
                color: '#DC2626',
                borderRadius: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={async () => {
                closeContextMenu();
                
                // Determine which items to delete
                const itemsToDelete = isSelectMode && selectedIds.size > 0
                  ? items.filter(item => selectedIds.has(item.id))
                  : contextItem ? [contextItem] : [];
                
                if (itemsToDelete.length === 0) {
                  alert('No items selected for deletion.');
                  return;
                }
                
                // Check if viewing a specific collection (not "All Items")
                const isInCollection = collectionProperties.id && inventoryState.collection !== 'all';
                
                // Check if user owns the current collection
                const ownsCollection = collectionProperties.owner_id && Number(collectionProperties.owner_id) === Number(user?.id);
                
                // Separate items by ownership
                // If user owns the collection, they can delete all items in it
                // Otherwise, they can only delete items they personally own
                // Note: If user_id is undefined, treat as owned by current user (legacy items or personal items)
                const canDelete = (item: any) => {
                  if (!item.user_id) return true; // Treat undefined user_id as owned by current user
                  return Number(item.user_id) === Number(user?.id) || (isInCollection && ownsCollection);
                };
                
                const deletableItems = itemsToDelete.filter(canDelete);
                const undeletableItems = itemsToDelete.filter(item => !canDelete(item));
                
                // Check if items are in other collections
                let itemsInOtherCollections: any[] = [];
                try {
                  const itemIds = deletableItems.map(item => item.id);
                  if (itemIds.length > 0) {
                    const response = await fetch(API_ENDPOINTS.GET_COLLECTION_MEMBERSHIP(itemIds), {
                      headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (response.ok) {
                      const data = await response.json();
                      // Filter to collections that contain at least one of our items, excluding current collection
                      itemsInOtherCollections = data.collections.filter((c: any) => 
                        c.count > 0 && c.id !== collectionProperties.id
                      );
                    }
                  }
                } catch (e) {
                  console.error('Failed to check collection membership:', e);
                }
                
                // Build confirmation message
                let confirmMsg = '';
                if (undeletableItems.length > 0) {
                  // Cannot delete items owned by others (unless we own the collection)
                  alert(`Cannot delete: ${undeletableItems.length} item${undeletableItems.length > 1 ? 's are' : ' is'} not owned by you.`);
                  return;
                } else {
                  // All items owned by user - always delete (move to trash)
                  const count = itemsToDelete.length;
                  const itemWord = count === 1 ? 'item' : 'items';
                  
                  if (itemsInOtherCollections.length > 0) {
                    // Warn about other collections, but still delete
                    const collectionNames = itemsInOtherCollections.slice(0, 3).map(c => c.name).join(', ');
                    const moreText = itemsInOtherCollections.length > 3 ? ` and ${itemsInOtherCollections.length - 3} more` : '';
                    confirmMsg = `Delete ${count} ${itemWord}?\n\n${count === 1 ? 'This item is' : 'These items are'} also in: ${collectionNames}${moreText}\n\n${count === 1 ? 'It' : 'They'} will be moved to trash.`;
                  } else {
                    confirmMsg = `Delete ${count} ${itemWord}?\n\n${count === 1 ? 'This item' : 'These items'} will be moved to trash.`;
                  }
                }
                
                if (!window.confirm(confirmMsg)) return;
                
                try {
                  let successCount = 0;
                  let errorCount = 0;
                  
                  for (const item of itemsToDelete) {
                    try {
                      await deleteNow(item.id, 'one');
                      successCount++;
                    } catch (e) {
                      console.error(`Failed to delete item ${item.id}:`, e);
                      errorCount++;
                    }
                  }
                  
                  if (errorCount > 0) {
                    alert(`Deleted ${successCount} items, but ${errorCount} failed.`);
                  }
                  
                  // Clear selection and exit manage mode
                  clearSelectionState();
                  setMode('view');
                  await fetchItems();
                } catch (e) {
                  console.error('Delete operation failed:', e);
                  alert('Failed to delete items');
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              <span>Delete…</span>
            </button>
            )}
          </div>
        </div>,
        document.body
      )}
      
      {showAddToCollectionModal && (
        <ManageCollectionsModal
          selectedItemIds={Array.from(selectedIds)}
          onClose={() => {
            setShowAddToCollectionModal(false);
            setCollectionActionMode(null);
          }}
          onSuccess={async () => {
            setShowAddToCollectionModal(false);
            
            // Invalidate cache after collection changes
            invalidateCollectionCache(inventoryState.collection);
            invalidateCountsCache();
            await refetchCounts();
            
            // If this was a "move" action, remove items from current collection
            if (collectionActionMode === 'move' && collectionProperties.id) {
              try {
                const itemIdsToRemove = Array.from(selectedIds);
                for (const itemId of itemIdsToRemove) {
                  await fetch(
                    API_ENDPOINTS.REMOVE_ITEM_FROM_COLLECTION(collectionProperties.id, itemId),
                    {
                      method: 'DELETE',
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                }
                // Refresh collection counts since items were moved
                setCollectionRefreshTrigger(prev => prev + 1);
              } catch (e) {
                console.error('Failed to remove items from source collection during move:', e);
              }
            }
            
            setCollectionActionMode(null);
            clearSelectionState();
            setMode('view');
            fetchItems();
          }}
        />
      )}
      
      
      <BulkEditModal
        open={showMoveModal}
        itemCount={selectedIds.size}
        onClose={() => setShowMoveModal(false)}
        token={token || ''}
        allCategories={allCategories}
        spaces={spaces}
        onConfirm={async (data) => {
          if (!token || selectedIds.size === 0) return;
          
          try {
            const selectedItems = items.filter(item => selectedIds.has(item.id));
            const updates = selectedItems.map(item => {
              const update: any = {
                id: item.id,
                name: item.name,
                description: item.description,
                color: item.color,
                quantity_min: item.quantity_min,
                space_id: data.spaceId !== undefined ? data.spaceId : item.space_id
              };
              
              if (!data.clearCategories && data.categoryIds.length > 0) {
                update.category_ids = data.categoryIds;
              } else if (data.clearCategories) {
                update.category_ids = [];
              }
              
              if (!data.clearArea && data.area) update.area = data.area;
              else if (data.clearArea) update.area = '';
              
              if (!data.clearLocation && data.location) update.location = data.location;
              else if (data.clearLocation) update.location = '';
              else update.location = item.location;
              
              // Handle sub-location - clear flag takes precedence
              if (data.clearSubLocation) update.sub_location = '';
              else if (data.subLocation) update.sub_location = data.subLocation;
              else update.sub_location = item.sub_location;
              
              // Handle purchase date - clear flag takes precedence
              if (data.clearPurchaseDate) update.purchased_at = null;
              else if (data.purchaseDate) update.purchased_at = data.purchaseDate;
              else update.purchased_at = item.purchased_at;
              
              return update;
            });
            
            const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ items: updates }),
            });
            
            if (!resp.ok) {
              const errorText = await resp.text();
              console.error('Bulk edit failed:', errorText);
              throw new Error('Bulk edit failed');
            }
            
            await fetchItems();
            setShowMoveModal(false);
            clearSelectionState();
            setMode('view'); // Exit select mode after edit
          } catch (e) {
            console.error('Bulk edit error:', e);
          }
        }}
      />
      
      {/* Unified Item Edit Modal - adapts to mobile/desktop automatically */}
      <UnifiedItemEditModal
        open={editOpen}
        item={editItem}
        onClose={() => setEditOpen(false)}
        onChange={(patch: Partial<EditableItem>) => {
          updateEditItem(patch);
        }}
        categories={categories.filter(c => c !== 'all')}
        saving={saving}
        saveDisabled={(() => {
          const originalItem = items.find(i => i.id === editItem?.id);
          const hasChanges = originalItem && editItem ? (
            String(editItem.name || '') !== String(originalItem.name || '') ||
            String(editItem.description || '') !== String(originalItem.description || '') ||
            JSON.stringify(editItem.categories?.map((c: any) => c.id).sort() || []) !== JSON.stringify(originalItem.categories?.map((c: any) => c.id).sort() || []) ||
            String(editItem.color || '') !== String(originalItem.color || '') ||
            String(editItem.area || '') !== String(originalItem.area || '') ||
            String(editItem.location || '') !== String(originalItem.location || '') ||
            String((editItem as any).sub_location || '') !== String((originalItem as any).sub_location || '') ||
            Number(editItem.quantity ?? 1) !== Number(originalItem.quantity ?? 1) ||
            Number(editItem.quantity_min ?? 1) !== Number(originalItem.quantity_min ?? 1) ||
            Number((editItem as any).sale_price ?? 0) !== Number((originalItem as any).sale_price ?? 0) ||
            Number((editItem as any).purchase_price ?? 0) !== Number((originalItem as any).purchase_price ?? 0) ||
            String((editItem as any).purchased_at || '') !== String((originalItem as any).purchased_at || '') ||
            String(editItem.condition || '') !== String(originalItem.condition || '')
          ) : (editItem?.id === -1);
          return !hasChanges;
        })()}
        readOnly={(() => {
          const currentItemData = items.find(i => i.id === editItem?.id);
          return currentItemData?.my_role === 'viewer';
        })()}
        onSave={async () => {
          const result = await saveEditItem({
            pendingImageId: (editItemRef.current as any)?.pendingImageId,
            duplicateCollectionId: (editItemRef.current as any)?.__duplicateCollectionId,
          });
          
          if (result.success) {
            closeEdit();
            
            // Scroll to the edited item after a brief delay to ensure DOM updates
            if (result.itemId) {
              setTimeout(() => {
                const itemElement = document.querySelector(`[data-item-id="${result.itemId}"]`);
                if (itemElement) {
                  itemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 300);
            }
          } else if (result.duplicateInfo) {
            setDuplicateDialogInfo(result.duplicateInfo);
          } else if (result.error && result.error !== 'Item limit reached') {
            alert(result.error);
          }
        }}
        token={token || undefined}
        onDelete={async (scope: 'one' | 'image') => {
          if (!token || !editItem) return;
          await deleteNow(editItem.id, scope);
        }}
        currentUserId={user?.id}
        isCollectionOwner={!!collectionProperties.id && inventoryState.collection !== 'all'}
        onReplacePhoto={handleReplacePhoto}
        onOpenImageSelection={async () => {
          if (editItem) {
            editItemRef.current = editItem;
            // Restore images from cache for this item (don't leave empty)
            const cached = getCachedImages(editItem.name);
            const cachedPhotos = getCachedPhotoData(editItem.name);
            if (cached && cached.length > 0 && cachedPhotos && cachedPhotos.length > 0) {
              setEditModalSearchImages(cached);
              setEditModalPhotoData(cachedPhotos);
            } else {
              // No cache for item name — fetch directly from Unsplash
              setEditModalSearchImages([]);
              setEditModalPhotoData([]);
              setImageSearchLoading(true);
              try {
                const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
                if (accessKey) {
                  const { buildUnsplashSearchUrl } = await import('../utils/unsplashUtils');
                  const searchUrl = buildUnsplashSearchUrl(editItem.name.trim(), accessKey);
                  const response = await fetch(searchUrl);
                  if (response.ok) {
                    const data = await response.json();
                    if (data.results && data.results.length > 0) {
                      const imageUrls = data.results.map((photo: any) => photo.urls.small);
                      setEditModalSearchImages(imageUrls);
                      setEditModalPhotoData(data.results);
                      setCachedImages(editItem.name, imageUrls);
                      setCachedPhotoData(editItem.name, data.results);
                    }
                  }
                }
              } catch (error) {
                console.error('Failed to fetch images:', error);
              } finally {
                setImageSearchLoading(false);
              }
            }
            setImageSearchPage(1);
          }
          setShowImageSelectionModal(true);
        }}
        onFilterByImage={(imgId: number) => {
          setSearchTerm('');
          setSelectedCategory('all');
          setImageFilterId(imgId);
          setEditOpen(false);
          requestAnimationFrame(() => {
            const mc = document.querySelector('.main-content') as HTMLElement | null;
            if (mc) {
              try { mc.scrollTo({ top: 0, behavior: 'smooth' }); }
              catch { mc.scrollTop = 0; }
            }
          });
        }}
        onAddItem={(imgId: number) => {
          if (!editItem) return;
          setEditOpen(false);
          const templateItem: EditableItem = {
            id: -1,
            name: '',
            description: '',
            categories: (items.find(i => i.id === editItem.id)?.categories || []).filter(cat => cat.key && cat.label).map(cat => ({
              id: cat.id,
              key: cat.key!,
              label: cat.label!
            })),
            color: '',
            area: editItem.area,
            location: editItem.location,
            quantity: editItem.quantity ?? 1,
            quantity_min: editItem.quantity_min ?? 1,
          };
          (templateItem as any).pendingImageId = imgId;
          setEditItem(templateItem);
          setEditOpen(true);
        }}
        onDuplicate={() => {
          if (!editItem) return;
          const duplicateItem: EditableItem = {
            id: -1,
            name: '',
            description: '',
            categories: [],
            color: '',
            area: editItem.area,
            location: editItem.location,
            sub_location: editItem.sub_location,
            photo_location: editItem.photo_location,
            quantity: editItem.quantity ?? 1,
            quantity_min: editItem.quantity_min ?? 1,
          };
          if (editImageId) {
            (duplicateItem as any).pendingImageId = editImageId;
          }
          setEditItem(duplicateItem);
        }}
        onShowAll={() => {
          setSearchTerm('');
          setSelectedCategory('all');
          setImageFilterId(null);
          setStockFilter('off');
          setEditOpen(false);
          requestAnimationFrame(() => {
            const mc = document.querySelector('.main-content') as HTMLElement | null;
            if (mc) {
              try { mc.scrollTo({ top: 0, behavior: 'smooth' }); }
              catch { mc.scrollTop = 0; }
            }
          });
        }}
        onPhotoUpload={(file: File) => {
          handlePhotoSelected(file, 'one');
        }}
        onShare={handleMobileShare}
        onDirectShare={handleDirectShare}
        onSearchImageSelect={async (itemId: number, imageUrl: string) => {
          if (!token) return;
          
          const item = items.find(i => i.id === itemId);
          if (!item) {
            console.error('Item not found:', itemId);
            return;
          }
            
            try {
              setSaving(true);
              const oldImageId = item.image_id;
              
              let newImageId = null;
              
              // If imageUrl is provided (not empty), create an image record with Wikimedia URL
              // Backend will store the URL directly without uploading to GCS
              if (imageUrl && imageUrl.trim()) {
                const imageResp = await fetch(API_ENDPOINTS.CREATE_IMAGE_FROM_URL, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    image_url: imageUrl,
                    original_filename: 'unsplash.jpg',
                    skip_upload: true, // Tell backend to NOT upload to GCS
                  }),
                });
                
                if (!imageResp.ok) {
                  const errorText = await imageResp.text();
                  console.error('Failed to create image record:', errorText);
                  throw new Error('Failed to create image record');
                }
                
                const imageData = await imageResp.json();
                newImageId = imageData.id;
              }
              
              // Update item with new image_id (or null for initials)
              const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  items: [{
                    id: item.id,
                    name: item.name,
                    description: item.description,
                    category_ids: item.categories?.map(c => c.id) || [],
                    color: item.color,
                    area: item.area,
                    location: item.location,
                    sub_location: item.sub_location,
                    quantity: item.quantity,
                    quantity_min: item.quantity_min,
                    image_id: newImageId, // Set to new image ID or null
                  }],
                }),
              });
              
              if (!resp.ok) throw new Error('Update failed');
              
              // Check if old image should be deleted
              if (oldImageId) {
                const itemsUsingImage = items.filter(i => i.image_id === oldImageId);
                if (itemsUsingImage.length === 1) {
                  // Only this item uses the image, delete it
                  try {
                    await fetch(API_ENDPOINTS.DELETE_IMAGE(oldImageId), {
                      method: 'DELETE',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                  } catch (e) {
                    console.error('Failed to delete unused image:', e);
                  }
                }
              }
              
              // Refresh items to get the updated image_id
              const freshItems = await fetchItems();
              
              // Update the editItem with the fresh data so the modal shows the new image
              // fetchItems returns the new items array
              if (freshItems) {
                const refreshedItem = freshItems.find((i: any) => i.id === itemId);
                if (refreshedItem) {
                  setEditItem(refreshedItem);
                  editItemRef.current = refreshedItem;
                }
              }
            } catch (e) {
              console.error('Failed to switch to Wikimedia image:', e);
              alert('Failed to update image. Please try again.');
            } finally {
              setSaving(false);
            }
          }}
        />

      {/* Image Selection Modal - shared by mobile edit and inventory card */}
      {showImageSelectionModal && editItem && (() => {
        const item = editItem; // TypeScript narrowing
        return (
          <ImageSelectionModal
            isOpen={showImageSelectionModal}
            onClose={() => {
              setShowImageSelectionModal(false);
              // Don't clear images - keep them cached for this item
            }}
            itemName={item.name || ''}
            searchImages={editModalSearchImages}
            initials={getInitials(item.name || '')}
            color1={getColorFromName(item.name || '', 0)}
            color2={getColorFromName(item.name || '', 12345)}
            currentImageId={editImageId}
            imageSourceType={(item as any).image_source_type}
            isLoading={imageSearchLoading}
            isLoadingMore={isLoadingMoreImages}
            onLoadMore={async () => {
              const currentItem = editItemRef.current;
              if (!currentItem?.name) return;
              
              const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
              if (!accessKey) return;
              
              const nextPage = imageSearchPage + 1;
              setIsLoadingMoreImages(true);
              
              try {
                const { buildUnsplashSearchUrl } = await import('../utils/unsplashUtils');
                const searchTerm = imageSearchTermOverride || currentItem.name.trim();
                const searchUrl = buildUnsplashSearchUrl(searchTerm, accessKey, 12, nextPage);
                
                const response = await fetch(searchUrl);
                if (!response.ok) throw new Error('Unsplash search failed');
                
                const data = await response.json();
                
                if (data.results && data.results.length > 0) {
                  const newUrls = data.results.map((photo: any) => photo.urls.small);
                  const allUrls = [...editModalSearchImages, ...newUrls];
                  const allPhotos = [...editModalPhotoData, ...data.results];
                  
                  setEditModalSearchImages(allUrls);
                  setEditModalPhotoData(allPhotos);
                  setImageSearchPage(nextPage);
                  
                  // Update cache with combined results
                  setCachedImages(currentItem.name, allUrls);
                  setCachedPhotoData(currentItem.name, allPhotos);
                }
              } catch (error) {
                console.error('Failed to load more images:', error);
              } finally {
                setIsLoadingMoreImages(false);
              }
            }}
          onSelectSearchImage={async (imageUrl: string, photoData?: any) => {
            const currentItem = editItemRef.current;
            
            if (!token || !currentItem || currentItem.id === -1) {
              return;
            }
            
            // Find the photo object that matches this URL
            const photo = editModalPhotoData.find((p: any) => p.urls.small === imageUrl);
            
            // Check if replacing a user-uploaded image with an Unsplash image
            const currentImageSourceType = (item as any).image_source_type;
            const isReplacingUserUpload = currentImageSourceType === 'user_upload' && photo;
            
            if (isReplacingUserUpload) {
              const confirmed = window.confirm(
                'You are about to replace your uploaded photo with an Unsplash image. This action cannot be undone. Continue?'
              );
              if (!confirmed) {
                return;
              }
            }
            
            try {
              setSaving(true);
              
              // Trigger Unsplash download endpoint (required by their terms)
              if (photo?.id) {
                const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
                if (accessKey) {
                  try {
                    await fetch(`https://api.unsplash.com/photos/${photo.id}/download?client_id=${accessKey}`);
                  } catch (e) {
                    console.warn('Failed to trigger Unsplash download endpoint:', e);
                  }
                }
              }
              
              // Build high-resolution URL for storage (using IMAGE_MAX_DIM from env)
              // Build thumbnail URL for performance (half of IMAGE_MAX_DIM)
              // Use urls.raw with custom dimensions for best quality
              // crop=faces,center: detects faces first, falls back to center crop for proper centering
              const imageMaxDim = parseInt(process.env.REACT_APP_IMAGE_MAX_DIM || '900');
              const thumbDim = Math.floor(imageMaxDim / 2);
              
              const highResUrl = photo?.urls?.raw 
                ? `${photo.urls.raw}&w=${imageMaxDim}&h=${imageMaxDim}&fit=crop&crop=faces,center&q=80`
                : imageUrl;
              
              const thumbnailUrl = photo?.urls?.raw
                ? `${photo.urls.raw}&w=${thumbDim}&h=${thumbDim}&fit=crop&crop=faces,center&q=80`
                : imageUrl;
              
              // Create image record with metadata (if available)
              const requestBody: any = {
                image_url: highResUrl,
                thumbnail_url: thumbnailUrl,
                original_filename: photo ? 'unsplash.jpg' : 'external.jpg',
                skip_upload: true,
              };
              
              // Only add Unsplash metadata if we have the photo object
              if (photo) {
                requestBody.unsplash_photo_id = photo.id;
                requestBody.photographer_name = photo.user?.name;
                requestBody.photographer_username = photo.user?.username;
                requestBody.unsplash_url = photo.links?.html;
              }
              
              const imageResp = await fetch(API_ENDPOINTS.CREATE_IMAGE_FROM_URL, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
              });
              
              if (!imageResp.ok) {
                const errorText = await imageResp.text();
                console.error('Image creation failed:', imageResp.status, errorText);
                throw new Error(`Failed to create image: ${imageResp.status}`);
              }
              
              const imageData = await imageResp.json();
              const newImageId = imageData.id;
              
              if (!newImageId) {
                console.error('No image ID returned:', imageData);
                throw new Error('No image ID returned from server');
              }
              
              const updatePayload = {
                items: [{
                  id: currentItem.id,
                  name: currentItem.name,
                  description: currentItem.description || '',
                  color: currentItem.color || '',
                  area: currentItem.area || '',
                  location: currentItem.location || '',
                  sub_location: currentItem.sub_location || '',
                  quantity: currentItem.quantity,
                  quantity_min: currentItem.quantity_min,
                  sale_price: (currentItem as any).sale_price || null,
                  purchase_price: currentItem.purchase_price,
                  purchased_at: currentItem.purchased_at,
                  image_id: newImageId,
                  category_ids: currentItem.categories?.map(c => c.id)
                }]
              };
              
              const updateResp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatePayload),
              });
              
              if (!updateResp.ok) {
                const errorText = await updateResp.text();
                console.error('Item update failed:', updateResp.status, errorText);
                throw new Error(`Failed to update item: ${updateResp.status}`);
              }
              
              await updateResp.json();
              
              // Refresh items from server
              await fetchItems();
              
              // Close modal
              setShowImageSelectionModal(false);
            } catch (e) {
              console.error('Image selection error:', e);
              alert('Failed to update image. Please try again.');
            } finally {
              setSaving(false);
            }
          }}
          onSelectInitials={async () => {
            const currentItem = editItemRef.current;
            if (!token || !currentItem || currentItem.id === -1) return;
            
            try {
              setSaving(true);
              await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  items: [{
                    id: currentItem.id,
                    name: currentItem.name,
                    image_id: null
                  }]
                }),
              });
              await fetchItems();
              setShowImageSelectionModal(false);
            } catch (e) {
              console.error('Remove image error:', e);
            } finally {
              setSaving(false);
            }
          }}
          onTakePhoto={async (file) => {
            const currentItem = editItemRef.current;
            if (!currentItem) return;
            const oldImageId = items.find(i => i.id === currentItem.id)?.image_id;
            const relatedCount = oldImageId ? items.filter(i => i.image_id === oldImageId).length : 1;
            setReplacePhotoItemCount(relatedCount);
            handlePhotoSelected(file, 'one');
            setShowImageSelectionModal(false);
          }}
          onSearchKeywords={async (keywords: string) => {
            if (!keywords.trim()) return;
            
            try {
              setImageSearchLoading(true);
              setImageSearchPage(1);
              setImageSearchTermOverride(keywords.trim());
              
              const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
              if (!accessKey) {
                console.error('Unsplash API key not configured');
                return;
              }
              
              const { buildUnsplashSearchUrl } = await import('../utils/unsplashUtils');
              const searchUrl = buildUnsplashSearchUrl(keywords.trim(), accessKey);
              
              const response = await fetch(searchUrl);
              if (!response.ok) {
                console.error('Unsplash API error:', response.status, response.statusText);
                throw new Error('Unsplash search failed');
              }
              
              const data = await response.json();
              
              if (data.results && data.results.length > 0) {
                const imageUrls = data.results.map((photo: any) => photo.urls.small);
                setEditModalSearchImages(imageUrls);
                setEditModalPhotoData(data.results);
              } else {
                setEditModalSearchImages([]);
                setEditModalPhotoData([]);
              }
            } catch (error) {
              console.error('Failed to fetch images for keywords:', error);
              alert('Failed to search images. Please try again.');
            } finally {
              setImageSearchLoading(false);
            }
          }}
        />
        );
      })()}

      {/* Photo Replacement Modal */}
      <PhotoReplacementModal
        isOpen={showPhotoReplacementModal}
        onClose={() => {
          setShowPhotoReplacementModal(false);
          setReplacePhotoScope(null);
        }}
        onPhotoSelected={handlePhotoSelected}
        itemCount={replacePhotoItemCount}
      />

      {/* Add Item Modal */}
      <AddItemModal
        isOpen={showAddItemModal}
        onClose={() => {
          setShowAddItemModal(false);
          if (pendingScrollItemIdRef.current !== null) {
            setScrollToItemId(pendingScrollItemIdRef.current);
            pendingScrollItemIdRef.current = null;
          }
        }}
        defaultTab={addItemModalDefaultTab}
        onSaveManual={async (itemData: NewItemData) => {
          if (!token) return;
          
          // If there's an image, upload it first to get image_id
          let imageId: number | undefined;
          if (itemData.imageFile) {
            try {
              const formData = new FormData();
              formData.append('image', itemData.imageFile);
              formData.append('create_image', 'true');
              formData.append('upload_only', 'true'); // Skip analysis, just upload
              
              const uploadResp = await fetch(API_ENDPOINTS.ANALYZE, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
              });
              
              if (uploadResp.ok) {
                const uploadData = await uploadResp.json();
                imageId = uploadData.image_id || uploadData.image?.id;
              } else if (uploadResp.status === 409) {
                // Duplicate image - use existing image_id
                const dupData = await uploadResp.json();
                imageId = dupData.existing_image_id || dupData.image_id;
              }
            } catch (err) {
              console.error('[AddItemModal] Image upload error:', err);
              // Continue without image if upload fails
            }
          }
          
          // Build the item for SAVE_ITEMS endpoint
          // Note: collection_id and category_ids must be inside the item object
          const item: any = {
            name: itemData.name,
            description: itemData.description,
            quantity: itemData.quantity,
            color: itemData.color,
            category_ids: itemData.categoryIds, // Backend expects snake_case
          };
          
          // Add image_id if we have one
          if (imageId) {
            item.image_id = imageId;
          }
          
          // If adding to a specific collection, include in item object
          if (collectionProperties.id && inventoryState.collection !== 'all') {
            item.collection_id = collectionProperties.id;
          }
          
          // Build request body
          const requestBody: any = {
            items: [item],
            area: itemData.area || '',
            location: itemData.location || '',
            space_id: activeSpace?.id,
          };
          
          // Auto-categorize if user has AI consent and didn't manually pick categories
          if ((!itemData.categoryIds || itemData.categoryIds.length === 0) && hasAIConsent(user?.id)) {
            requestBody.auto_categorize = true;
          }
          
          const response = await fetch(API_ENDPOINTS.SAVE_ITEMS, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            if (response.status === 409 && errorData.duplicate_item_id) {
              // Show duplicate dialog instead of error
              setDuplicateDialogInfo({
                duplicate_item_id: errorData.duplicate_item_id,
                duplicate_item_name: errorData.duplicate_item_name,
                duplicate_item_quantity: errorData.duplicate_item_quantity ?? 1,
                duplicate_collection_ids: errorData.duplicate_collection_ids ?? [],
                newItemData: {
                  name: item.name,
                  description: item.description,
                  quantity: item.quantity,
                  color: item.color,
                  category_ids: item.category_ids,
                  image_id: item.image_id,
                },
              });
              return; // Don't throw — dialog will handle it
            }
            throw new Error(errorData.error || 'Failed to add item');
          }
          
          // Capture new item ID and scroll to it
          const respData = await response.json();
          const newItemId = respData.saved_items?.[0]?.id;
          invalidateCountsCache();
          await fetchItems();
          await refetchCounts();
          if (newItemId) {
            setScrollToItemId(newItemId);
          }
        }}
        onScanComplete={() => fetchItems()}
        token={token || ''}
        defaultQuantity={stockChecklistMode === 'manage' ? 0 : 1}
        collectionId={collectionProperties.id}
        collectionName={inventoryState.collection !== 'all' ? collectionName : undefined}
        onInventoryRefresh={async (newItemId?: number) => {
          invalidateCountsCache();
          await fetchItems();
          await refetchCounts();
          if (newItemId) pendingScrollItemIdRef.current = newItemId;
        }}
        onBarcodeDuplicate={(info) => setDuplicateDialogInfo(info)}
      />

      {/* Add Missing Items Modal */}
      <AddMissingItemsModal
        isOpen={showAddMissingModal}
        onClose={() => setShowAddMissingModal(false)}
        missingTerms={unmatchedTerms}
        collectionId={collectionProperties.id}
        collectionName={inventoryState.collection !== 'all' ? collectionName : undefined}
        onItemsAdded={async () => {
          invalidateCountsCache();
          await fetchItems();
          await refetchCounts();
        }}
      />

      {/* Manage Sharing Modal (unified groups + email sharing) */}
      <ManageSharingModal
        isOpen={showManageSharingModal}
        onClose={() => {
          clearSelection();  // Clear selection when closing modal
          setShowManageSharingModal(false);
          setManageSharingItemName(undefined);
          setManageSharingItemIds([]);
        }}
        selectedItems={manageSharingItemIds}
        itemName={manageSharingItemName}
        onSuccess={() => {
          fetchItems();
          clearSelection();  // Clear selection after share/unshare
          setShowManageSharingModal(false);
          setManageSharingItemName(undefined);
          setManageSharingItemIds([]);
        }}
      />

      {/* QR Assignment Modal */}
      <QRAssignModal
        isOpen={showQRAssignModal}
        onClose={() => {
          setShowQRAssignModal(false);
        }}
        selectedItemIds={Array.from(selectedIds)}
        onAssign={handleQRAssign}
        onShowToast={(message) => {
          setToastMessage(message);
          setShowToast(true);
        }}
      />

      {/* Auction Setup Modal */}
      {showAuctionSetupModal && auctionSetupItemId && token && (
        <AuctionSetupModal
          itemId={auctionSetupItemId}
          itemName={auctionSetupItemName}
          token={token}
          itemSalePrice={auctionSetupSalePrice}
          onClose={() => {
            setShowAuctionSetupModal(false);
            setAuctionSetupItemId(null);
          }}
          onCreated={() => {
            setShowAuctionSetupModal(false);
            setAuctionSetupItemId(null);
            setToastMessage('Auction created and published!');
            setToastColor(undefined);
            setShowToast(true);
          }}
        />
      )}

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        color={toastColor}
      />

      {/* Collection Modal (create/edit) */}
      <CollectionModal
        isOpen={showCollectionModal}
        onClose={() => {
          setShowCollectionModal(false);
          setSelectedCollectionId(undefined);
          setSelectedCollectionName('');
        }}
        collectionId={selectedCollectionId}
        initialName={selectedCollectionName}
        onSave={async (id, name, description, collectionType) => {
          if (!token) return;
          try {
            if (selectedCollectionId) {
              // Update existing collection
              const response = await fetch(API_ENDPOINTS.UPDATE_COLLECTION(selectedCollectionId), {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  name,
                  description,
                  collection_type: collectionType,
                  space_id: activeSpace?.id
                })
              });

              if (response.ok) {
                setShowCollectionModal(false);
                // Invalidate stale cache so the properties effect re-fetches fresh data
                collectionPropsCache.delete(String(selectedCollectionId));
                setCollectionRefreshTrigger(prev => prev + 1);
                // Update display immediately
                setCollectionName(name);
                setCollectionDescription(description);
                // Update collection properties
                setCollectionProperties(prev => ({
                  ...prev,
                  name,
                  description,
                  collection_type: collectionType,
                  space_id: activeSpace?.id
                }));
                // Clear initialization flag to force view mode re-evaluation
                delete hasInitializedRef.current[inventoryState.collection];
                fetchItems();
                window.dispatchEvent(new CustomEvent('collections-updated'));
              }
            } else {
              // Create new collection
              const response = await fetch(API_ENDPOINTS.CREATE_COLLECTION, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  name,
                  description,
                  collection_type: collectionType,
                  space_id: activeSpace?.id
                })
              });

              if (response.ok) {
                setShowCollectionModal(false);
                setCollectionRefreshTrigger(prev => prev + 1);
                fetchItems();
                window.dispatchEvent(new CustomEvent('collections-updated'));
              }
            }
          } catch (error) {
            console.error('Failed to save collection:', error);
          }
        }}
        onShare={selectedCollectionId ? (id, collectionType) => {
          setSharingCollectionId(id);
          setSharingCollectionType(collectionType);
          setShowCollectionModal(false);
        } : undefined}
        onDelete={selectedCollectionId ? async (id, name) => {
          await handleDeleteCollection(id, name);
          setShowCollectionModal(false);
          setSelectedCollectionId(undefined);
        } : undefined}
      />

      {/* Duplicate Item Dialog */}
      <DuplicateItemDialog
        isOpen={duplicateDialogInfo !== null}
        onClose={() => setDuplicateDialogInfo(null)}
        duplicateInfo={duplicateDialogInfo || { duplicate_item_id: 0, duplicate_item_name: '', duplicate_item_quantity: 1, duplicate_collection_ids: [] }}
        currentCollectionId={inventoryState.collection !== 'all' ? collectionProperties.id : undefined}
        currentCollectionName={inventoryState.collection !== 'all' ? collectionName : undefined}
        onKeepExisting={handleDuplicateKeep}
        onReplaceWithNew={handleDuplicateReplace}
        onIncreaseQuantity={handleDuplicateIncreaseQuantity}
      />

      {/* Collection Share Modal */}
      {sharingCollectionId !== null && (
        <CollectionShareModal
          isOpen={true}
          onClose={() => setSharingCollectionId(null)}
          collection={{
            id: sharingCollectionId,
            name: collectionName || '',
            collection_type: sharingCollectionType || collectionProperties.collection_type,
            marketplace_visible: collectionProperties.marketplace_visible
          }}
          onSuccess={() => {
            setSharingCollectionId(null);
            setCollectionRefreshTrigger(prev => prev + 1);
            fetchItems();
            window.dispatchEvent(new CustomEvent('collections-updated'));
          }}
        />
      )}
    </PageTemplate>
    </>
  );
};

export default InventoryView;
